import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Lock, RefreshCw, LogOut, ExternalLink, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import InboxPanel from "@/components/InboxPanel";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All admin timestamps display in IST so the times stay consistent regardless
// of where the admin signs in from. Backend stores everything in UTC as ISO
// strings — this only changes the display.
//
// IMPORTANT: backend uses `datetime.utcnow().isoformat()` which produces a
// naive ISO string with NO timezone designator (e.g. "2026-06-05T13:21:28").
// Per ECMA-262, `new Date(...)` parses such strings as **local time**, not UTC.
// We coerce naive strings to UTC by appending "Z" so the IST conversion is
// accurate regardless of where the admin's browser is located.
const IST_TZ = "Asia/Kolkata";
const toUtcDate = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  // If the string already has a timezone designator (Z, +05:30, -08:00 etc.)
  // leave it alone. Otherwise, treat it as UTC.
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  return new Date(hasTz ? s : s + "Z");
};
const fmtIST = (iso, opts = { dateStyle: "medium", timeStyle: "short" }) => {
  if (!iso) return "—";
  try {
    const d = toUtcDate(iso);
    if (!d || isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", { ...opts, timeZone: IST_TZ });
  } catch {
    return "—";
  }
};
const fmtISTShortDate = (iso) => fmtIST(iso, { day: "2-digit", month: "short", timeZone: IST_TZ });

// Live IST clock for the admin header.
const useNowIST = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleString("en-IN", {
    timeZone: IST_TZ,
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const AdminClock = () => {
  const nowIST = useNowIST();
  return (
    <div
      className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"
      data-testid="admin-current-ist"
    >
      <Clock className="h-3 w-3" />
      <span className="font-mono tabular-nums">{nowIST} IST</span>
    </div>
  );
};

const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-600",
  pending: "bg-yellow-100 text-yellow-800",
  payment_success: "bg-blue-100 text-blue-800",
  order_placed: "bg-indigo-100 text-indigo-800",
  processing: "bg-purple-100 text-purple-800",
  order_sent_to_fulfillment: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  fulfilled: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-800",
};

const ORDER_STATUS_OPTIONS = [
  "draft",
  "pending",
  "payment_success",
  "order_placed",
  "processing",
  "order_sent_to_fulfillment",
  "completed",
  "fulfilled",
  "failed",
];

const AdminPage = () => {
  const [password, setPassword] = useState(() =>
    sessionStorage.getItem("admin_password") || ""
  );
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("admin_password"));
  const [verifying, setVerifying] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    hero_image_url: "",
    before_image_url: "",
    after_image_url: "",
    renaissance_hero_image_url: "",
    default_coupon_code: "",
  });
  const [pickerSlot, setPickerSlot] = useState(null); // "hero" | "before" | "after" | "renaissance_hero" | null
  const [promptVariants, setPromptVariants] = useState([]);
  const [promptDefault, setPromptDefault] = useState("");
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponDiscount, setNewCouponDiscount] = useState("");
  const [newCouponType, setNewCouponType] = useState("flat"); // "flat" | "percentage"
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [collections, setCollections] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [tab, setTab] = useState("orders"); // "orders" | "generations" | "settings" | "prompts" | "coupons" | "analytics" | "renaissance"

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    try {
      setVerifying(true);
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem("admin_password", password);
      setAuthed(true);
      toast.success("Admin access granted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid password");
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_password");
    setAuthed(false);
    setPassword("");
    setOrders([]);
    setStats(null);
    setGenerations([]);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { "X-Admin-Password": password };
      const [ordersRes, statsRes, gensRes, settingsRes, promptsRes, couponsRes, analyticsRes, collectionsRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/generations`, { headers }),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/admin/prompts`, { headers }),
        axios.get(`${API}/admin/coupons`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/collections`, { headers }),
      ]);
      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data);
      setGenerations(gensRes.data.items || []);
      setSiteSettings({
        hero_image_url: settingsRes.data.hero_image_url || "",
        before_image_url: settingsRes.data.before_image_url || "",
        after_image_url: settingsRes.data.after_image_url || "",
        renaissance_hero_image_url: settingsRes.data.renaissance_hero_image_url || "",
        default_coupon_code: settingsRes.data.default_coupon_code || "",
      });
      setPromptDefault(promptsRes.data.default_prompt || "");
      const variants = promptsRes.data.prompts || [];
      // If admin hasn't configured any, seed the editor with the default as variant 1
      if (variants.length === 0) {
        setPromptVariants([
          {
            id: "default",
            name: "Custom Pet Portrait",
            prompt: promptsRes.data.default_prompt || "",
            _isDefault: true,
          },
        ]);
      } else {
        setPromptVariants(variants);
      }
      setCoupons(couponsRes.data.items || []);
      setAnalytics(analyticsRes.data || null);
      setCollections(collectionsRes.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Users list is fetched on first visit to the Users tab — kept out of the
  // initial parallel boot so login stays fast (large customer bases shouldn't
  // slow the admin dashboard's first paint).
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await axios.get(`${API}/admin/users`, {
        headers: { "X-Admin-Password": password },
      });
      setUsers(res.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        handleLogout();
      } else {
        toast.error("Failed to fetch users");
      }
    } finally {
      setUsersLoading(false);
    }
  };

  // Lazy-fetch the users list the first time the admin opens the tab.
  useEffect(() => {
    if (tab === "users" && authed && users.length === 0 && !usersLoading) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authed]);

  const createCoupon = async () => {
    const code = newCouponCode.trim().toUpperCase();
    const value = parseFloat(newCouponDiscount);
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    if (!value || value <= 0) {
      toast.error("Enter a discount value greater than 0");
      return;
    }
    if (newCouponType === "percentage" && value > 100) {
      toast.error("Percentage must be between 1 and 100");
      return;
    }
    try {
      setCreatingCoupon(true);
      const res = await axios.post(
        `${API}/admin/coupons`,
        { code, type: newCouponType, value },
        { headers: { "X-Admin-Password": password } }
      );
      setCoupons((prev) => [res.data.coupon, ...prev]);
      setNewCouponCode("");
      setNewCouponDiscount("");
      setNewCouponType("flat");
      toast.success(`Coupon ${code} created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create coupon");
    } finally {
      setCreatingCoupon(false);
    }
  };

  const deleteCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}? Existing orders that already used it are unaffected.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/coupons/${encodeURIComponent(code)}`, {
        headers: { "X-Admin-Password": password },
      });
      setCoupons((prev) => prev.filter((c) => c.code !== code));
      toast.success(`Coupon ${code} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const updateVariant = (idx, key, value) => {
    setPromptVariants((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value, _isDefault: false };
      return next;
    });
  };

  const addVariant = () => {
    setPromptVariants((prev) => {
      if (prev.length >= 8) {
        toast.error("Maximum 8 variants. Each one is a separate Gemini call.");
        return prev;
      }
      return [
        ...prev,
        {
          id: `v_${Date.now()}`,
          name: `Variant ${prev.length + 1}`,
          prompt: promptDefault,
          _isDefault: false,
        },
      ];
    });
  };

  const removeVariant = (idx) => {
    setPromptVariants((prev) => {
      if (prev.length <= 1) {
        toast.error("At least one variant is required.");
        return prev;
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const savePrompts = async (revertToDefault = false) => {
    try {
      setSavingPrompts(true);
      const body = revertToDefault
        ? { prompts: [] }
        : {
            prompts: promptVariants.map((v) => ({
              id: v.id,
              name: v.name,
              prompt: v.prompt,
            })),
          };
      const res = await axios.put(`${API}/admin/prompts`, body, {
        headers: { "X-Admin-Password": password },
      });
      const variants = res.data.prompts || [];
      if (variants.length === 0) {
        setPromptVariants([
          {
            id: "default",
            name: "Custom Pet Portrait",
            prompt: promptDefault,
            _isDefault: true,
          },
        ]);
      } else {
        setPromptVariants(variants);
      }
      toast.success(
        revertToDefault
          ? "Reverted to default (single variant)"
          : `Saved ${variants.length} variant${variants.length === 1 ? "" : "s"}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save prompts");
    } finally {
      setSavingPrompts(false);
    }
  };

  const saveSiteSetting = async (slot, url) => {
    const fieldMap = {
      hero: "hero_image_url",
      before: "before_image_url",
      after: "after_image_url",
      renaissance_hero: "renaissance_hero_image_url",
    };
    const field = fieldMap[slot];
    if (!field) return;
    try {
      const res = await axios.put(
        `${API}/admin/site-settings`,
        { [field]: url },
        { headers: { "X-Admin-Password": password } }
      );
      setSiteSettings({
        hero_image_url: res.data.hero_image_url || "",
        before_image_url: res.data.before_image_url || "",
        after_image_url: res.data.after_image_url || "",
        renaissance_hero_image_url: res.data.renaissance_hero_image_url || "",
        default_coupon_code: res.data.default_coupon_code || "",
      });
      toast.success("Homepage updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  // Admin picks (or clears) the coupon that auto-applies for every customer
  // at checkout. Pass "" to disable the default; pass an existing coupon
  // code (e.g. "WELCOME10") to turn it on. Backend validates the code exists.
  const saveDefaultCheckoutCoupon = async (code) => {
    try {
      const res = await axios.put(
        `${API}/admin/site-settings`,
        { default_coupon_code: code || "" },
        { headers: { "X-Admin-Password": password } }
      );
      setSiteSettings((prev) => ({
        ...prev,
        default_coupon_code: res.data.default_coupon_code || "",
      }));
      toast.success(
        code
          ? `Default coupon set to ${code}`
          : "Default coupon cleared — no auto-apply"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save default coupon");
    }
  };

  const toggleShowcase = async (jobId, styleId, current) => {
    try {
      await axios.patch(
        `${API}/admin/generations/${jobId}/showcase`,
        { is_showcase: !current, style_id: styleId },
        { headers: { "X-Admin-Password": password } }
      );
      setGenerations((prev) =>
        prev.map((g) =>
          g.job_id === jobId && g.style_id === styleId
            ? { ...g, is_showcase: !current }
            : g
        )
      );
      toast.success(
        !current ? "Featured on homepage gallery" : "Removed from gallery"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const resubmitOrder = async (orderId) => {
    if (!window.confirm("Resubmit this order to Qikink? Use this only when the order is paid but stuck (no Qikink ID).")) return;
    const overrideUrl = window.prompt(
      "Optional: paste a public design URL to use as the Qikink design_link. Leave blank to auto-recover from existing data on the order.",
      ""
    );
    try {
      const res = await axios.post(
        `${API}/admin/orders/${orderId}/resubmit`,
        { selected_style_image_url: overrideUrl || undefined },
        { headers: { "X-Admin-Password": password } }
      );
      toast.success(`Resubmitted. Design URL: ${res.data.selected_style_image_url}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resubmit failed");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { headers: { "X-Admin-Password": password } }
      );
      setOrders((prev) =>
        prev.map((o) => (o.order_id === orderId ? { ...o, status } : o))
      );
      toast.success(`Order marked ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const createCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) {
      toast.error("Enter a collection name");
      return;
    }
    try {
      setCreatingCollection(true);
      const res = await axios.post(
        `${API}/admin/collections`,
        { name },
        { headers: { "X-Admin-Password": password } }
      );
      setCollections((prev) => [...prev, res.data.collection]);
      setNewCollectionName("");
      toast.success(`Collection ${name} created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create collection");
    } finally {
      setCreatingCollection(false);
    }
  };

  const updateCollection = async (cid, patch) => {
    try {
      const res = await axios.patch(
        `${API}/admin/collections/${cid}`,
        patch,
        { headers: { "X-Admin-Password": password } }
      );
      setCollections((prev) =>
        prev.map((c) => (c.id === cid ? res.data.collection : c))
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update collection");
    }
  };

  const reorderCollections = async (ids) => {
    // Optimistic: reorder client-side first, then persist.
    setCollections((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
      // Append any collections not included (defensive)
      const remaining = prev.filter((c) => !ids.includes(c.id));
      return [...ordered, ...remaining];
    });
    try {
      const res = await axios.post(
        `${API}/admin/collections/reorder`,
        { ids },
        { headers: { "X-Admin-Password": password } }
      );
      setCollections(res.data.items || []);
      toast.success("Order saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save order");
      // Re-fetch to restore truth on failure
      fetchData();
    }
  };

  useEffect(() => {
    if (authed) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  if (!authed) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center px-4"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={handleLogin}
          className="bg-card border border-border/60 p-10 w-full max-w-md"
        >
          <div className="flex items-center justify-center mb-6">
            <Lock className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-black tracking-tight text-center mb-2">
            Admin Access
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Enter your admin password to continue
          </p>
          <Label htmlFor="admin-password" className="label-uppercase mb-2 block">
            Password
          </Label>
          <Input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="mb-6"
            autoFocus
          />
          <Button
            type="submit"
            disabled={verifying || !password.trim()}
            className="w-full h-12 text-base font-bold"
            data-testid="admin-login-button"
          >
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Verifying...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="glass-header py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-black tracking-tight truncate">
              Admin · theSnugglePrints
            </h1>
            <AdminClock />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            <StatCard label="Total Orders" value={stats.total_orders} />
            <StatCard
              label="Revenue (Paid)"
              value={`₹${(stats.total_revenue || 0).toLocaleString("en-IN")}`}
            />
            <StatCard
              label="Pending"
              value={stats.status_breakdown?.pending || 0}
            />
            <StatCard
              label="Processing"
              value={
                (stats.status_breakdown?.processing || 0) +
                (stats.status_breakdown?.order_placed || 0) +
                (stats.status_breakdown?.order_sent_to_fulfillment || 0)
              }
            />
          </div>
        )}

        {/* Sidebar + content: tabs moved from a horizontal bar into a
            sticky left rail so the workspace has full vertical real
            estate for panels with dense grids (Products, Photo Books,
            etc.). Content column uses `min-w-0` so nested grids don't
            explode past the viewport width. */}
        {(() => {
          const TABS = [
            { id: "orders", label: `Orders (${orders.length})`, testid: "admin-tab-orders" },
            { id: "inbox", label: "Inbox", testid: "admin-tab-inbox" },
            { id: "generations", label: `Generations (${generations.length})`, testid: "admin-tab-generations" },
            { id: "settings", label: "Site Settings", testid: "admin-tab-settings" },
            { id: "homepage", label: "Homepage", testid: "admin-tab-homepage" },
            { id: "prompt", label: `Modern Styles (${promptVariants.length})`, testid: "admin-tab-prompt" },
            { id: "coupons", label: `Coupons (${coupons.length})`, testid: "admin-tab-coupons" },
            { id: "users", label: `Users (${users.length})`, testid: "admin-tab-users" },
            { id: "analytics", label: "Analytics", testid: "admin-tab-analytics" },
            { id: "sessions", label: "Sessions", testid: "admin-tab-sessions" },
            { id: "renaissance_styles", label: "Renaissance Styles", testid: "admin-tab-renaissance-styles" },
            { id: "ipl_styles", label: "IPL Styles", testid: "admin-tab-ipl-styles" },
            { id: "collections", label: `Collections (${collections.length})`, testid: "admin-tab-collections" },
            { id: "promo", label: "Promo Pages", testid: "admin-tab-promo" },
            { id: "testimonials", label: "Testimonials", testid: "admin-tab-testimonials" },
            { id: "sizes", label: "Products", testid: "admin-tab-sizes" },
            { id: "photo_books", label: "Photo Books", testid: "admin-tab-photo-books" },
          ];
          return (
            <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
              <aside
                className="lg:sticky lg:top-24 self-start border-b lg:border-b-0 lg:border-r border-border pb-4 lg:pb-0 lg:pr-4 max-h-[calc(100vh-7rem)] overflow-y-auto"
                data-testid="admin-tab-sidebar"
              >
                <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`whitespace-nowrap lg:whitespace-normal text-left px-3 py-2 text-sm rounded-md transition-colors flex-shrink-0 lg:flex-shrink lg:w-full ${
                        tab === t.id
                          ? "bg-primary/10 text-foreground font-medium border-l-2 lg:border-l-2 border-primary"
                          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-l-2 border-transparent"
                      }`}
                      data-testid={t.testid}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </aside>
              <div className="min-w-0">

        {tab === "analytics" ? (
          <AnalyticsPanel analytics={analytics} loading={loading} adminPassword={password} apiBase={API} />
        ) : tab === "inbox" ? (
          <InboxPanel adminPassword={password} apiBase={BACKEND_URL} />
        ) : tab === "sessions" ? (
          <SessionsPanel adminPassword={password} apiBase={API} />
        ) : tab === "sizes" ? (
          <SizesPanel adminPassword={password} apiBase={API} />
        ) : tab === "photo_books" ? (
          <PhotoBooksAdminPanel adminPassword={password} apiBase={API} />
        ) : tab === "renaissance_styles" ? (
          <RenaissanceStylesPanel
            adminPassword={password}
            apiBase={API}
            generations={generations}
          />
        ) : tab === "ipl_styles" ? (
          <IPLStylesPanel
            adminPassword={password}
            apiBase={API}
            generations={generations}
          />
        ) : tab === "collections" ? (
          <CollectionsPanel
            collections={collections}
            newName={newCollectionName}
            setNewName={setNewCollectionName}
            creating={creatingCollection}
            onCreate={createCollection}
            onUpdate={updateCollection}
            onReorder={reorderCollections}
            adminPassword={password}
            apiBase={API}
            generations={generations}
          />
        ) : tab === "promo" ? (
          <PromoPagesPanel
            adminPassword={password}
            apiBase={API}
            collections={collections}
            generations={generations}
          />
        ) : tab === "testimonials" ? (
          <TestimonialsPanel
            adminPassword={password}
            apiBase={API}
            generations={generations}
          />
        ) : tab === "orders" ? (
          /* Orders */
          <div className="bg-card border border-border/60 p-6 sm:p-8">
          <h2 className="text-2xl font-bold mb-6">Recent Orders</h2>

          {loading && orders.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">
              No orders yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-orders-table">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="label-uppercase py-3 pr-4">Ref #</th>
                    <th className="label-uppercase py-3 pr-4">Customer</th>
                    <th className="label-uppercase py-3 pr-4">Pet</th>
                    <th className="label-uppercase py-3 pr-4">Amount</th>
                    <th className="label-uppercase py-3 pr-4">Status</th>
                    <th className="label-uppercase py-3 pr-4">Date</th>
                    <th className="label-uppercase py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <OrderRow
                      key={o.order_id}
                      order={o}
                      expanded={expandedOrder === o.order_id}
                      onToggle={() =>
                        setExpandedOrder(
                          expandedOrder === o.order_id ? null : o.order_id
                        )
                      }
                      onResubmit={resubmitOrder}
                      onStatusChange={updateOrderStatus}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        ) : tab === "generations" ? (
          /* Generations */
          <GenerationsGrid
            items={generations}
            loading={loading}
            onToggleShowcase={toggleShowcase}
          />
        ) : tab === "settings" ? (
          /* Site Settings */
          <SiteSettingsPanel
            settings={siteSettings}
            onPick={setPickerSlot}
            onClear={(slot) => saveSiteSetting(slot, "")}
          />
        ) : tab === "homepage" ? (
          /* Homepage editor — copy + section reorder + show/hide */
          <HomepagePanel adminPassword={password} apiBase={API} />
        ) : tab === "prompt" ? (
          /* Modern Portrait Styles — same generic editor as Renaissance/IPL */
          <ModernStylesPanel
            adminPassword={password}
            apiBase={API}
            generations={generations}
          />
        ) : tab === "coupons" ? (
          /* Coupons */
          <CouponsPanel
            coupons={coupons}
            newCode={newCouponCode}
            setNewCode={setNewCouponCode}
            newDiscount={newCouponDiscount}
            setNewDiscount={setNewCouponDiscount}
            newType={newCouponType}
            setNewType={setNewCouponType}
            creating={creatingCoupon}
            onCreate={createCoupon}
            onDelete={deleteCoupon}
            adminPassword={password}
            defaultCheckoutCoupon={siteSettings.default_coupon_code || ""}
            onSaveDefaultCheckoutCoupon={saveDefaultCheckoutCoupon}
          />
        ) : (
          /* Users */
          <UsersPanel users={users} loading={usersLoading} onRefresh={fetchUsers} />
        )}
              </div>
            </div>
          );
        })()}
      </main>

      {pickerSlot && (
        <ImagePickerModal
          slot={pickerSlot}
          generations={generations}
          onClose={() => setPickerSlot(null)}
          onSelect={(url) => {
            saveSiteSetting(pickerSlot, url);
            setPickerSlot(null);
          }}
        />
      )}
    </div>
  );
};

const GenerationsGrid = ({ items, loading, onToggleShowcase }) => {
  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="bg-card border border-border/60 p-12 text-center text-muted-foreground">
        No generations yet.
      </div>
    );
  }
  return (
    <div>
      <div className="bg-card border border-border/60 p-6 mb-6">
        <h2 className="text-2xl font-bold mb-2">Every Generation</h2>
        <p className="text-sm text-muted-foreground">
          Click the star to feature a portrait on the homepage gallery. Only
          starred portraits appear publicly.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((g) => (
          <GenerationCard
            key={`${g.job_id}-${g.style_id || g.variant_index || 0}`}
            g={g}
            onToggle={onToggleShowcase}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Small image cell used in the Generations cards. Renders an empty state when
 * the URL is missing and links to the full-size image in a new tab. `fit`
 * controls how the image sits inside the square: 'cover' for photos,
 * 'contain' for art posters (so the full poster is visible without crop).
 */
const Thumb = ({ label, url, fallback = "—", fit = "cover", testid }) => (
  <div data-testid={testid}>
    <div className="label-uppercase mb-1">{label}</div>
    {url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={label}
          className={`w-full aspect-square ${
            fit === "contain"
              ? "object-contain bg-white"
              : "object-cover bg-muted/30"
          } border border-border`}
        />
      </a>
    ) : (
      <div className="w-full aspect-square bg-muted/30 border border-border flex items-center justify-center text-xs text-muted-foreground">
        {fallback}
      </div>
    )}
  </div>
);

const GenerationCard = ({ g, onToggle }) => {
  const created = fmtIST(g.created_at);
  const completed = g.status === "completed";
  const failed = g.status === "failed" || !!g.error;
  const variantLabel =
    g.variant_count && g.variant_count > 1
      ? `${g.style_name || `Variant ${g.variant_index + 1}`} · ${g.variant_index + 1}/${g.variant_count}`
      : g.style_name || null;
  const cardKey = `${g.job_id}-${g.style_id || g.variant_index || 0}`;
  return (
    <div
      className={`bg-card border p-4 ${
        g.is_showcase ? "border-primary border-2" : "border-border/60"
      }`}
      data-testid={`generation-card-${cardKey}`}
    >
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Thumb
          label="Original"
          url={g.original_url}
          fallback="not stored"
          fit="cover"
          testid={`generation-original-${cardKey}`}
        />
        <Thumb
          label="Full image"
          url={g.image_url}
          fallback={g.status === "failed" ? "failed" : "processing"}
          fit="contain"
          testid={`generation-full-${cardKey}`}
        />
        <Thumb
          label="Framed"
          url={g.preview_url}
          fallback={g.status === "failed" ? "failed" : "processing"}
          fit="contain"
          testid={`generation-framed-${cardKey}`}
        />
      </div>
      <div className="text-sm font-medium mb-1">
        {g.pet_name || "(no name)"}
      </div>
      {variantLabel && (
        <div className="text-xs text-[#C8A27A] font-bold mb-1 uppercase tracking-wide">
          {variantLabel}
        </div>
      )}
      <div className="text-xs text-muted-foreground mb-3 line-clamp-1">
        {g.pet_description || "—"}
      </div>
      <div className="text-xs text-muted-foreground mb-3">{created}</div>
      {failed && g.error && (
        <div
          className="text-[11px] bg-red-50 border border-red-200 text-red-800 px-2 py-1.5 mb-3 font-mono whitespace-pre-wrap break-words rounded"
          data-testid={`generation-error-${cardKey}`}
          title={g.error}
        >
          {g.error.length > 240 ? `${g.error.slice(0, 240)}…` : g.error}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${
          g.status === "completed" ? "bg-green-100 text-green-800" :
          g.status === "failed" ? "bg-red-100 text-red-800" :
          "bg-yellow-100 text-yellow-800"
        }`}>
          {g.status}
        </span>
        <button
          onClick={() => onToggle(g.job_id, g.style_id, g.is_showcase)}
          disabled={!completed || !g.preview_url}
          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border transition-colors disabled:opacity-30 ${
            g.is_showcase
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-transparent border-border hover:border-primary"
          }`}
          data-testid={`showcase-toggle-${cardKey}`}
        >
          <Star className={`mr-1 h-3 w-3 ${g.is_showcase ? "fill-current" : ""}`} />
          {g.is_showcase ? "Featured" : "Feature"}
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-card border border-border/60 p-6">
    <div className="label-uppercase text-muted-foreground mb-2">{label}</div>
    <div className="text-3xl font-black tracking-tight">{value}</div>
  </div>
);

const OrderRow = ({ order, expanded, onToggle, onResubmit, onStatusChange }) => {
  const statusClass = STATUS_STYLES[order.status] || "bg-gray-100 text-gray-800";
  const createdAt = fmtIST(order.created_at);

  return (
    <>
      <tr className="border-b border-border/40 hover:bg-muted/30 transition-colors">
        <td className="py-3 pr-4 font-mono text-xs">{order.reference_number}</td>
        <td className="py-3 pr-4">
          <div className="font-medium">{order.customer?.name}</div>
          <div className="text-xs text-muted-foreground">
            {order.customer?.email}
          </div>
        </td>
        <td className="py-3 pr-4 font-medium">
          {order.pet_name || order.selected_style || "—"}
        </td>
        <td className="py-3 pr-4 font-bold">
          ₹{(order.total_amount || order.retail_price)?.toLocaleString("en-IN") || 0}
        </td>
        <td className="py-3 pr-4">
          <select
            value={order.status}
            onChange={(e) =>
              onStatusChange && onStatusChange(order.order_id, e.target.value)
            }
            className={`text-xs font-bold rounded px-2 py-1 border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary ${statusClass}`}
            data-testid={`order-status-select-${order.order_id}`}
          >
            {ORDER_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </td>
        <td className="py-3 pr-4 text-xs text-muted-foreground">{createdAt}</td>
        <td className="py-3">
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? "Hide" : "Details"}
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20">
          <td colSpan="7" className="py-6 px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="label-uppercase text-muted-foreground mb-2">
                  Shipping Address
                </div>
                <div className="text-sm space-y-1">
                  <div>{order.customer?.phone}</div>
                  <div>{order.customer?.address1}</div>
                  {order.customer?.address2 && (
                    <div>{order.customer.address2}</div>
                  )}
                  <div>
                    {order.customer?.city}, {order.customer?.state}{" "}
                    {order.customer?.pincode}
                  </div>
                  <div>{order.customer?.country}</div>
                </div>
              </div>
              <div>
                <div className="label-uppercase text-muted-foreground mb-2">
                  Payment
                </div>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-muted-foreground">Razorpay Order: </span>
                    <span className="font-mono text-xs">
                      {order.razorpay_order_id || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Payment ID: </span>
                    <span className="font-mono text-xs">
                      {order.razorpay_payment_id || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Qikink Order: </span>
                    <span className="font-mono text-xs">
                      {order.qikink_order_id || "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <div className="label-uppercase text-muted-foreground mb-2">
                  Design
                </div>
                {order.selected_style_image_url ? (
                  <a
                    href={order.selected_style_image_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium underline inline-flex items-center"
                  >
                    View design
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
                {order.error_message && (
                  <p className="text-xs text-red-600 mt-2 break-words">
                    {order.error_message}
                  </p>
                )}
                {!order.qikink_order_id &&
                  ["payment_success", "order_placed", "processing", "pending", "failed"].includes(order.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => onResubmit && onResubmit(order.order_id)}
                    data-testid={`resubmit-order-${order.order_id}`}
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Resubmit to Qikink
                  </Button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/* ---- Site Settings Tab ---- */

const SLOT_LABELS = {
  hero: "Hero image (Minimalist card on homepage)",
  before: "Before image (your photo)",
  after: "After image (your portrait)",
  renaissance_hero: "Renaissance hero image (Renaissance card on homepage)",
};

const SLOT_HINTS = {
  hero:
    "Shown big at the top of the homepage on the Minimalist category card. We recommend a framed mockup (use the framed preview).",
  before:
    "Shown as the 'Before' photo in the Before / After section. Use the customer's uploaded original.",
  after:
    "Shown as the 'After' portrait in the Before / After section. Use the generated portrait.",
  renaissance_hero:
    "Shown as the thumbnail on the Renaissance category card on the homepage. Pick a finished Renaissance portrait that represents the collection.",
};

const SiteSettingsPanel = ({ settings, onPick, onClear }) => {
  const slots = [
    { key: "hero", url: settings.hero_image_url },
    { key: "renaissance_hero", url: settings.renaissance_hero_image_url },
    { key: "before", url: settings.before_image_url },
    { key: "after", url: settings.after_image_url },
  ];
  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="site-settings-panel">
      <h2 className="text-2xl font-bold mb-2">Homepage Images</h2>
      <p className="text-sm text-muted-foreground mb-8">
        Choose which images appear on your homepage. Click "Change" to pick from any
        generation; the selection is saved instantly and live on the homepage.
      </p>

      <div className="space-y-6">
        {slots.map(({ key, url }) => (
          <div key={key} className="border border-border/60 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-32 h-32 bg-muted/30 border border-border/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                {url ? (
                  <img
                    src={url}
                    alt={SLOT_LABELS[key]}
                    className="w-full h-full object-cover"
                    data-testid={`site-${key}-preview`}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground text-center px-2">
                    Using default
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="font-bold mb-1">{SLOT_LABELS[key]}</div>
                <p className="text-sm text-muted-foreground mb-4">{SLOT_HINTS[key]}</p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => onPick(key)}
                    data-testid={`site-${key}-pick`}
                  >
                    Change
                  </Button>
                  {url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onClear(key)}
                      data-testid={`site-${key}-clear`}
                    >
                      Reset to default
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Image Picker Modal ---- */

const ImagePickerModal = ({ slot, slotLabel, slotHint, generations, onClose, onSelect, mode = "generated", allowUpload = false, adminPassword, apiBase }) => {
  // `mode`:
  //   - "generated" (default): show the framed previews so admin can pick any
  //     of the customer-visible portraits.
  //   - "raw": also include the raw image_url (no frame) alongside the framed
  //     preview, useful for style/coming-soon thumbnail slots.
  //   - "original": only show the uploaded source photos.
  // For 'before' the legacy code used `slot === "before"` to flip to source
  // photos — keep that working by mapping it to `mode='original'`.
  const effectiveMode = slot === "before" ? "original" : mode;
  const items = [];
  if (effectiveMode === "original") {
    const seen = new Set();
    for (const g of generations) {
      const url = g.original_url;
      if (!url || seen.has(g.job_id)) continue;
      seen.add(g.job_id);
      items.push({
        url,
        label: g.pet_name || g.job_id?.slice(0, 8) || "",
        id: g.job_id,
      });
    }
  } else {
    for (const g of generations) {
      const framed = g.preview_url;
      const raw = g.image_url;
      const variantTag =
        g.variant_count && g.variant_count > 1
          ? ` · ${g.style_name || `V${g.variant_index + 1}`}`
          : "";
      const baseLabel = `${g.pet_name || g.job_id?.slice(0, 8) || ""}${variantTag}`;
      if (framed) {
        items.push({
          url: framed,
          label: `${baseLabel} · Framed`,
          id: `${g.job_id}-${g.style_id || g.variant_index || 0}-framed`,
        });
      }
      if (effectiveMode === "raw" && raw && raw !== framed) {
        items.push({
          url: raw,
          label: `${baseLabel} · Raw`,
          id: `${g.job_id}-${g.style_id || g.variant_index || 0}-raw`,
        });
      }
    }
  }

  const [customUrl, setCustomUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const onUploadFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (!allowUpload || !apiBase) return;
    try {
      setUploading(true);
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${apiBase}/admin/upload`, form, {
        headers: {
          "X-Admin-Password": adminPassword,
          "Content-Type": "multipart/form-data",
        },
      });
      const url = res.data?.url;
      if (!url) throw new Error("No URL returned");
      toast.success("Image uploaded");
      onSelect(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-picker-modal"
    >
      <div
        className="bg-background border border-border w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Pick image · {slotLabel || SLOT_LABELS[slot]}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {slotHint || SLOT_HINTS[slot]}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="picker-close">
            Close
          </Button>
        </div>

        <div className="p-6 border-b border-border">
          {allowUpload && (
            <div className="mb-4">
              <Label className="label-uppercase mb-2 block">Upload from computer</Label>
              <div className="flex items-center gap-3">
                <label
                  className={`inline-flex items-center justify-center h-10 px-4 border border-border/60 bg-card cursor-pointer text-sm font-medium hover:border-primary transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="picker-upload-label"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    "Choose file"
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onUploadFile}
                    disabled={uploading}
                    data-testid="picker-upload-input"
                  />
                </label>
                <span className="text-xs text-muted-foreground">PNG / JPG / WEBP up to ~10 MB</span>
              </div>
            </div>
          )}
          <Label className="label-uppercase mb-2 block">Or paste an image URL</Label>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://..."
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              data-testid="picker-custom-url"
            />
            <Button
              onClick={() => customUrl && onSelect(customUrl.trim())}
              disabled={!customUrl.trim()}
              data-testid="picker-custom-url-save"
            >
              Use this URL
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">
              No suitable images yet. Generate at least one portrait first.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => onSelect(it.url)}
                  className="group bg-card border border-border/60 hover:border-primary p-2 text-left transition-colors"
                  data-testid={`picker-item-${it.id}`}
                >
                  <div className="aspect-square bg-muted/30 overflow-hidden mb-2">
                    <img
                      src={it.url}
                      alt={it.label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  {it.label && (
                    <div className="text-xs font-medium truncate px-1">{it.label}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---- Prompts Tab (multi-variant) ---- */

const PromptsPanel = ({
  variants,
  defaultPrompt,
  saving,
  onUpdate,
  onAdd,
  onRemove,
  onSave,
  onReset,
}) => {
  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="prompts-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <h2 className="text-2xl font-bold mb-2">Minimalist Styles</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Each style is one prompt and produces one portrait variant the
            customer can pick from. Add up to 8 — for example, "Watercolor",
            "Cartoon", "Photorealistic". Use{" "}
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
              {"{pet_name}"}
            </code>{" "}
            wherever the customer's pet name should appear.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAdd}
            disabled={saving || variants.length >= 8}
            data-testid="prompt-add-variant"
          >
            + Add variant
          </Button>
        </div>
      </div>

      <div className="text-xs text-amber-700 mb-6 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
        Heads-up: each variant is a separate Gemini call. Three variants ≈ 3× the
        cost &amp; time per customer order.
      </div>

      <div className="space-y-4">
        {variants.map((v, idx) => (
          <PromptVariantCard
            key={v.id || idx}
            idx={idx}
            variant={v}
            canDelete={variants.length > 1}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onLoadDefault={() => onUpdate(idx, "prompt", defaultPrompt)}
          />
        ))}
      </div>

      <div className="flex gap-3 mt-6 flex-wrap items-center">
        <Button onClick={onSave} disabled={saving} data-testid="prompts-save">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            `Save ${variants.length} variant${variants.length === 1 ? "" : "s"}`
          )}
        </Button>
        <Button
          variant="ghost"
          onClick={onReset}
          disabled={saving}
          data-testid="prompts-reset"
        >
          Revert to built-in default (single variant)
        </Button>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Changes apply to the next generation. The customer sees these as a grid to
        pick from before checkout.
      </p>
    </div>
  );
};

const PromptVariantCard = ({ idx, variant, canDelete, onUpdate, onRemove, onLoadDefault }) => {
  const charCount = variant.prompt?.length || 0;
  const hasPlaceholder = variant.prompt?.includes("{pet_name}");
  return (
    <div
      className="border border-border/60 bg-background/40 p-4 sm:p-6"
      data-testid={`prompt-variant-${idx}`}
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="serif text-2xl text-[#C8A27A] mr-1">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <Input
          value={variant.name}
          onChange={(e) => onUpdate(idx, "name", e.target.value)}
          className="max-w-xs font-bold"
          placeholder="Style name (e.g. Watercolor)"
          data-testid={`prompt-variant-${idx}-name`}
        />
        <span className="text-xs text-muted-foreground">{charCount} chars</span>
        <label className="text-xs flex items-center gap-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={variant.active !== false}
            onChange={(e) => onUpdate(idx, "active", e.target.checked)}
            data-testid={`prompt-variant-${idx}-active`}
          />
          Active
        </label>
        <label className="text-xs flex items-center gap-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!variant.featured}
            onChange={(e) => onUpdate(idx, "featured", e.target.checked)}
            data-testid={`prompt-variant-${idx}-featured`}
          />
          Featured
        </label>
        {!hasPlaceholder && (
          <span className="text-xs text-amber-700 font-medium">
            Missing {"{pet_name}"} placeholder
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadDefault}
            data-testid={`prompt-variant-${idx}-load-default`}
          >
            Load default
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(idx)}
              className="text-red-700 hover:text-red-800"
              data-testid={`prompt-variant-${idx}-remove`}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <textarea
        value={variant.prompt}
        onChange={(e) => onUpdate(idx, "prompt", e.target.value)}
        rows={14}
        className="w-full font-mono text-sm bg-background border border-border/60 p-4 focus:outline-none focus:border-primary resize-y"
        data-testid={`prompt-variant-${idx}-textarea`}
        placeholder="Prompt for this variant..."
      />
    </div>
  );
};

/* ---- Users Tab ---- */

const UsersPanel = ({ users, loading, onRefresh }) => {
  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="users-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-2xl font-bold">Signed-up Users</h2>
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="users-refresh"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Refreshing…
            </>
          ) : (
            "Refresh"
          )}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Everyone who has created an account on theSnugglePrints. Portrait and
        order counts are computed live (drafts excluded).
      </p>

      {loading && users.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
        </div>
      ) : users.length === 0 ? (
        <div className="text-center text-muted-foreground py-10 border border-dashed border-border/60">
          No users have signed up yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 px-4 label-uppercase">Name</th>
                <th className="py-3 px-4 label-uppercase">Email</th>
                <th className="py-3 px-4 label-uppercase">Phone</th>
                <th className="py-3 px-4 label-uppercase">Portraits</th>
                <th className="py-3 px-4 label-uppercase">Orders</th>
                <th className="py-3 px-4 label-uppercase">Signed up (IST)</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-border/40 hover:bg-muted/30"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="py-3 px-4">
                    {u.name || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 px-4 break-all">
                    {u.email || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    {u.phone || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 px-4 font-medium" data-testid={`user-portraits-${u.id}`}>
                    {u.portraits_count}
                  </td>
                  <td className="py-3 px-4 font-medium" data-testid={`user-orders-${u.id}`}>
                    {u.orders_count}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                    {fmtIST(u.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


/* ---- Coupons Tab ---- */

const CouponsPanel = ({
  coupons,
  newCode,
  setNewCode,
  newDiscount,
  setNewDiscount,
  newType,
  setNewType,
  creating,
  onCreate,
  onDelete,
  adminPassword,
  defaultCheckoutCoupon,
  onSaveDefaultCheckoutCoupon,
}) => {
  const isPct = newType === "percentage";
  const [usageCode, setUsageCode] = useState(null);
  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="coupons-panel">
      <h2 className="text-2xl font-bold mb-2">Discount Coupons</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Create flat-amount (₹) or percentage (%) discount codes. Customers enter the code at checkout and the total reduces accordingly.
      </p>

      {/* Default checkout coupon — auto-applied for every customer */}
      <div
        className="border border-border/60 p-4 sm:p-6 mb-6 bg-[#FAF6EF]"
        data-testid="default-checkout-coupon-section"
      >
        <h3 className="font-bold mb-1">Auto-apply at checkout</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Pick one coupon to apply automatically for every customer who lands
          on the checkout page. Leave as <em>None</em> for no auto-apply.
          Customers can still remove it manually.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <select
            value={defaultCheckoutCoupon || ""}
            onChange={(e) => onSaveDefaultCheckoutCoupon(e.target.value)}
            disabled={!onSaveDefaultCheckoutCoupon}
            className="w-full sm:w-72 h-10 px-3 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="default-checkout-coupon-select"
          >
            <option value="">None — no auto-apply</option>
            {coupons.map((c) => {
              const t = c.type || "flat";
              const v = c.value ?? c.discount ?? 0;
              const label = t === "percentage" ? `${v}% off` : `₹${v} off`;
              return (
                <option key={c.code} value={c.code}>
                  {c.code} ({label})
                </option>
              );
            })}
          </select>
          {defaultCheckoutCoupon ? (
            <span
              className="text-xs text-[#1A1A1A]"
              data-testid="default-checkout-coupon-current"
            >
              Currently auto-applying:{" "}
              <strong>{defaultCheckoutCoupon}</strong>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              No coupon is auto-applied right now.
            </span>
          )}
        </div>
      </div>

      {/* Create form */}
      <div className="border border-border/60 p-4 sm:p-6 mb-6">
        <h3 className="font-bold mb-4">Add a new coupon</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Label htmlFor="new-coupon-code" className="label-uppercase mb-2 block">
              Code
            </Label>
            <Input
              id="new-coupon-code"
              type="text"
              placeholder="WELCOME100"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              className="uppercase"
              disabled={creating}
              data-testid="new-coupon-code"
            />
          </div>
          <div>
            <Label htmlFor="new-coupon-type" className="label-uppercase mb-2 block">
              Type
            </Label>
            <select
              id="new-coupon-type"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              disabled={creating}
              className="w-full h-10 px-3 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="new-coupon-type"
            >
              <option value="flat">Flat (₹)</option>
              <option value="percentage">Percentage (%)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="new-coupon-discount" className="label-uppercase mb-2 block">
              {isPct ? "Discount (%)" : "Discount (₹)"}
            </Label>
            <Input
              id="new-coupon-discount"
              type="number"
              min="1"
              max={isPct ? "100" : "10000"}
              step="1"
              placeholder={isPct ? "10" : "100"}
              value={newDiscount}
              onChange={(e) => setNewDiscount(e.target.value)}
              disabled={creating}
              data-testid="new-coupon-discount"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onCreate}
              disabled={creating}
              className="w-full"
              data-testid="new-coupon-submit"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add coupon"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Existing coupons */}
      {coupons.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 border border-dashed border-border/60">
          No coupons yet. Add one above.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 px-4 label-uppercase">Code</th>
                <th className="py-3 px-4 label-uppercase">Discount</th>
                <th className="py-3 px-4 label-uppercase">Created (IST)</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const t = c.type || "flat";
                const v = c.value != null ? c.value : c.discount;
                const display = t === "percentage" ? `${v}%` : `₹${v}`;
                return (
                  <tr
                    key={c.code}
                    className="border-b border-border/60"
                    data-testid={`coupon-row-${c.code}`}
                  >
                    <td className="py-3 px-4 font-mono font-bold">{c.code}</td>
                    <td className="py-3 px-4">{display}</td>
                    <td className="py-3 px-4 text-muted-foreground text-xs">
                      {fmtIST(c.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUsageCode(c.code)}
                        data-testid={`coupon-usage-${c.code}`}
                      >
                        View usage
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(c.code)}
                        className="text-red-700 hover:text-red-800"
                        data-testid={`coupon-delete-${c.code}`}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {usageCode && (
        <CouponUsageModal
          code={usageCode}
          adminPassword={adminPassword}
          onClose={() => setUsageCode(null)}
        />
      )}
    </div>
  );
};

const CouponUsageModal = ({ code, adminPassword, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/admin/coupons/${encodeURIComponent(code)}/usage`, {
        headers: { "X-Admin-Password": adminPassword },
      })
      .then((res) => active && setData(res.data))
      .catch((err) =>
        active && setError(err.response?.data?.detail || "Failed to load usage")
      );
    return () => {
      active = false;
    };
  }, [code, adminPassword]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`coupon-usage-modal-${code}`}
    >
      <div
        className="bg-background w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border/60">
          <div>
            <p className="label-uppercase mb-1">Coupon usage</p>
            <h3 className="serif text-2xl text-[#1A1A1A]">{code}</h3>
            {data && (
              <p className="text-xs text-muted-foreground mt-1">
                Used <span className="font-bold">{data.count}</span> times · total savings{" "}
                <span className="font-bold">₹{(data.total_savings || 0).toLocaleString("en-IN")}</span>
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="p-6">
          {error ? (
            <div className="text-sm text-red-700">{error}</div>
          ) : !data ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : data.count === 0 ? (
            <div className="text-center text-muted-foreground py-8 border border-dashed border-border/60">
              No orders have used this coupon yet.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid={`coupon-usage-table-${code}`}>
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 px-3 label-uppercase">Date (IST)</th>
                  <th className="py-2 px-3 label-uppercase">Ref</th>
                  <th className="py-2 px-3 label-uppercase">Customer</th>
                  <th className="py-2 px-3 label-uppercase">Status</th>
                  <th className="py-2 px-3 label-uppercase text-right">Saved</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => (
                  <tr
                    key={o.order_id}
                    className="border-b border-border/60"
                    data-testid={`coupon-usage-row-${o.order_id}`}
                  >
                    <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtIST(o.created_at)}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{o.reference_number}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{o.customer_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.customer_email || ""}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-bold rounded ${STATUS_STYLES[o.status] || "bg-gray-100 text-gray-700"}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-bold">
                      ₹{(o.coupon_discount || 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---- Analytics Tab ---- */

const STATUS_COLOR = {
  payment_success: "bg-green-100 text-green-800",
  order_placed: "bg-blue-100 text-blue-800",
  processing: "bg-yellow-100 text-yellow-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
};

const AnalyticsPanel = ({ analytics, loading, adminPassword, apiBase }) => {
  if (!analytics) {
    return (
      <div className="bg-card border border-border/60 p-12 text-center">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        ) : (
          <span className="text-muted-foreground">No analytics yet.</span>
        )}
      </div>
    );
  }
  const { pageviews, unique_sessions, generations, orders, funnel, recent_orders } = analytics;
  return (
    <div data-testid="analytics-panel">
      {/* Today's snapshot */}
      <h2 className="text-2xl font-bold mb-4">Today</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Pageviews" value={pageviews.today} />
        <StatCard label="Unique Visitors" value={unique_sessions.today} />
        <StatCard label="Generations" value={generations.today} />
        <StatCard label="Orders" value={orders.today} />
      </div>

      {/* All time */}
      <h2 className="text-2xl font-bold mb-4">All Time</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Pageviews" value={pageviews.total} />
        <StatCard label="Unique Visitors" value={unique_sessions.total} />
        <StatCard label="Generations Started" value={generations.total} />
        <StatCard label="Paid Orders" value={orders.paid} />
      </div>

      {/* 30-day time series */}
      <TimeseriesChart data={analytics.timeseries || []} />

      {/* Funnel View — detailed stage-by-stage drop-off */}
      <div className="bg-card border border-border/60 p-6 sm:p-8 mb-8" data-testid="funnel-view">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <h2 className="text-2xl font-bold">Conversion Funnel</h2>
          <span className="text-xs text-muted-foreground">
            Visit → Pay overall:{" "}
            <strong className="text-foreground">{funnel.view_to_pay_pct}%</strong>
          </span>
        </div>
        <FunnelChart stages={funnel.stages || []} />
      </div>

      {/* Backend Health — pod restart log */}
      <BackendHealthPanel adminPassword={adminPassword} apiBase={apiBase} />

      {/* Who placed orders */}
      <div className="bg-card border border-border/60 p-6 sm:p-8">
        <h2 className="text-2xl font-bold mb-2">Who placed orders</h2>
        <p className="text-sm text-muted-foreground mb-6">
          The 25 most recent paid orders. For full details use the "Orders" tab.
        </p>
        {!recent_orders || recent_orders.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 border border-dashed border-border/60">
            No paid orders yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-orders-table">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-3 px-4 label-uppercase">Customer</th>
                  <th className="py-3 px-4 label-uppercase">Pet</th>
                  <th className="py-3 px-4 label-uppercase">City</th>
                  <th className="py-3 px-4 label-uppercase">Amount</th>
                  <th className="py-3 px-4 label-uppercase">Coupon</th>
                  <th className="py-3 px-4 label-uppercase">Status</th>
                  <th className="py-3 px-4 label-uppercase">When</th>
                </tr>
              </thead>
              <tbody>
                {recent_orders.map((o) => (
                  <tr
                    key={o.order_id}
                    className="border-b border-border/60"
                    data-testid={`analytics-order-${o.order_id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium">{o.customer_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {o.customer_email || ""}
                      </div>
                    </td>
                    <td className="py-3 px-4">{o.pet_name || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {o.customer_city || "—"}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      ₹{Math.round(o.total_amount ?? o.retail_price ?? 0)}
                    </td>
                    <td className="py-3 px-4 text-xs font-mono">
                      {o.coupon_code || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-bold rounded ${
                          STATUS_COLOR[o.status] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">
                      {fmtIST(o.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TimeseriesChart = ({ data }) => {
  const [hidden, setHidden] = useState({});
  const series = [
    { key: "pageviews", name: "Pageviews", color: "#C8A27A" },
    { key: "unique_visitors", name: "Unique Visitors", color: "#3F6B4D" },
    { key: "generations", name: "Generations", color: "#4A6FA5" },
    { key: "orders", name: "Orders", color: "#B75D47" },
  ];
  // Nicer X axis labels: MM/DD
  const chartData = data.map((d) => ({
    ...d,
    label: d.date.slice(5), // "MM-DD"
  }));
  const hasAnyData = chartData.some(
    (d) => d.pageviews || d.unique_visitors || d.generations || d.orders
  );
  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8 mb-8">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Last 30 Days</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Click a series in the legend to hide it.
          </p>
        </div>
      </div>

      {!hasAnyData ? (
        <div className="text-center text-muted-foreground py-16 border border-dashed border-border/60">
          No activity in the last 30 days yet. Come back after some traffic rolls in.
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: -8 }}>
              <CartesianGrid stroke="#EAE5DF" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#6B6B6B", fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: "#6B6B6B", fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid #EAE5DF",
                  fontSize: 12,
                }}
                labelFormatter={(lab, payload) => {
                  const item = payload?.[0]?.payload;
                  return item?.date || lab;
                }}
              />
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 13, paddingTop: 8 }}
                onClick={(o) =>
                  setHidden((h) => ({ ...h, [o.dataKey]: !h[o.dataKey] }))
                }
              />
              {series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  hide={!!hidden[s.key]}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const FunnelRow = ({ label, count, total, pct, pctOf }) => {
  const width = total > 0 ? Math.max(2, Math.round((count / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm">
          <strong>{count}</strong>
          {pct !== undefined && (
            <span className="text-muted-foreground ml-2 text-xs">
              {pct}% {pctOf}
            </span>
          )}
        </span>
      </div>
      <div className="w-full bg-muted/40 h-2 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

/* ---- Backend Health: pod restart log ---- */
const BackendHealthPanel = ({ adminPassword, apiBase }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/system-events?limit=20`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtGap = (sec) => {
    if (sec === null || sec === undefined) return "—";
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
    return `${(sec / 86400).toFixed(1)}d`;
  };

  const items = data?.items || [];

  return (
    <div
      className="bg-card border border-border/60 p-6 sm:p-8 mb-8"
      data-testid="backend-health-panel"
    >
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Backend Health</h2>
        <button
          onClick={load}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="backend-health-refresh"
        >
          Refresh
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Every time the backend pod boots (deploy or OOM restart) a row is
        written here. If you see a tight cluster of restarts with short gaps,
        the pod is being killed — usually OOM.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Restarts (24h)" value={data?.restarts_24h ?? 0} />
            <StatCard label="Restarts (7d)" value={data?.restarts_7d ?? 0} />
            <StatCard
              label="Last RSS @ boot"
              value={items[0]?.rss_mb_at_boot ? `${items[0].rss_mb_at_boot} MB` : "—"}
            />
            <StatCard
              label="Current PID"
              value={items[0]?.pid ?? "—"}
            />
          </div>
          {items.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              No boot events recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="backend-health-table"
              >
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-3 px-2 label-uppercase">Boot Time (IST)</th>
                    <th className="py-3 px-2 label-uppercase">PID</th>
                    <th className="py-3 px-2 label-uppercase">RSS @ boot</th>
                    <th className="py-3 px-2 label-uppercase">Gap since prev</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const gap = it.gap_seconds;
                    const suspect = gap !== null && gap !== undefined && gap < 300; // <5m between restarts is suspicious
                    return (
                      <tr
                        key={`${it.created_at}-${it.pid}`}
                        className={`border-b border-border/60 ${suspect ? "bg-red-50" : ""}`}
                        data-testid={`backend-health-row-${i}`}
                      >
                        <td className="py-2 px-2 text-xs whitespace-nowrap">
                          {fmtIST(it.created_at)}
                        </td>
                        <td className="py-2 px-2 font-mono text-xs">{it.pid}</td>
                        <td className="py-2 px-2 text-xs">
                          {it.rss_mb_at_boot} MB
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {fmtGap(gap)}
                          {suspect && (
                            <span className="ml-2 text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                              SHORT GAP
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ---- Funnel View: detailed stage drop-off chart ---- */
const FunnelChart = ({ stages }) => {
  if (!stages || stages.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-6">
        No funnel data yet.
      </p>
    );
  }
  const top = stages[0]?.value || 0;
  const fmtPct = (n, d) => {
    if (!d || d <= 0) return "0%";
    return `${((n / d) * 100).toFixed(1)}%`;
  };
  return (
    <div className="space-y-3" data-testid="funnel-chart">
      {stages.map((s, i) => {
        const prev = i === 0 ? null : stages[i - 1];
        const widthPct =
          top > 0 ? Math.max(2, Math.round((s.value / top) * 100)) : 0;
        const conversionFromPrev = prev ? fmtPct(s.value, prev.value) : null;
        const dropoff = prev ? prev.value - s.value : 0;
        return (
          <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
            <div className="flex items-baseline justify-between mb-1 gap-2 flex-wrap">
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-muted-foreground font-mono w-5">
                  {i + 1}.
                </span>
                <span className="text-sm font-medium">{s.label}</span>
                {prev && (
                  <span className="text-xs text-muted-foreground">
                    {conversionFromPrev} from “{prev.label}”
                    {dropoff > 0 && (
                      <span className="text-red-600 ml-2">
                        −{dropoff} dropped
                      </span>
                    )}
                  </span>
                )}
              </div>
              <span className="text-sm">
                <strong>{s.value}</strong>
                {top > 0 && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    {fmtPct(s.value, top)} of landed
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-muted/40 h-3 rounded-sm overflow-hidden">
              <div
                className={`h-full transition-all ${
                  i === 0
                    ? "bg-primary"
                    : i === stages.length - 1
                    ? "bg-emerald-600"
                    : "bg-primary/70"
                }`}
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ---- Renaissance Styles Tab ---- */
const RenaissanceStylesPanel = (props) => (
  <CollectionStylesPanel
    {...props}
    collectionId="renaissance"
    title="Renaissance Styles"
    description="Six selectable Renaissance themes. Add or remove styles, mark any as 'Coming soon', and edit the prompt variants the customer sees."
    testid="renaissance-styles-panel"
  />
);

/* ---- IPL Styles Tab ---- */
const IPLStylesPanel = (props) => (
  <CollectionStylesPanel
    {...props}
    collectionId="ipl"
    title="IPL Styles"
    description="Each team has its own style. Add or remove teams, mark any as 'Coming soon', and edit the prompt variants."
    testid="ipl-styles-panel"
  />
);

/* ---- Modern Styles Tab (legacy 'prompts' tab) ---- */
const ModernStylesPanel = (props) => (
  <CollectionStylesPanel
    {...props}
    collectionId="minimalist"
    title="Modern Portrait Styles"
    description="Modern Portrait styles. Each customer-side style generates 2 portraits (single prompt is duplicated, two prompts run side-by-side)."
    testid="modern-styles-panel"
  />
);

/* ---- Shared panel: lists all styles in a collection + add/delete ---- */
const CollectionStylesPanel = ({ adminPassword, apiBase, generations, collectionId, title, description, testid }) => {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const headers = { "X-Admin-Password": adminPassword };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/styles`, { headers });
      setStyles((res.data.items || []).filter((s) => s.collection_id === collectionId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load styles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  const onStyleChanged = (updated) => {
    setStyles((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };
  const onStyleDeleted = (sid) => {
    setStyles((prev) => prev.filter((s) => s.id !== sid));
  };

  const moveStyle = async (idx, dir) => {
    const sorted = [...styles].sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
    // Optimistic update with new sort_order values
    const reindexed = sorted.map((s, i) => ({ ...s, sort_order: i }));
    setStyles(reindexed);
    try {
      const res = await axios.post(
        `${apiBase}/admin/styles/reorder`,
        { collection_id: collectionId, ids: sorted.map((s) => s.id) },
        { headers }
      );
      setStyles(res.data.items || reindexed);
      toast.success("Order saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save order");
      load(); // restore truth from server on failure
    }
  };

  const addStyle = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a name");
      return;
    }
    try {
      setAdding(true);
      const res = await axios.post(
        `${apiBase}/admin/styles`,
        {
          collection_id: collectionId,
          name,
          prompt: "Describe the new style prompt here...",
          coming_soon: false,
        },
        { headers }
      );
      setStyles((prev) => [...prev, res.data.style]);
      setNewName("");
      toast.success(`${name} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add style");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid={testid}>
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <p className="text-sm text-muted-foreground mb-6">{description}</p>

      <div className="border border-border/60 p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <Label className="label-uppercase mb-2 block">Add a new type</Label>
          <Input
            placeholder="e.g. Steampunk Hero"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={adding}
            data-testid={`${testid}-new-name`}
          />
        </div>
        <Button onClick={addStyle} disabled={adding} data-testid={`${testid}-add`}>
          {adding ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            "+ Add type"
          )}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : styles.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 border border-dashed border-border/60">
          No styles yet. Add the first one above.
        </div>
      ) : (
        <div className="space-y-8">
          {[...styles]
            .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999))
            .map((s, idx, arr) => (
              <StyleSection
                key={s.id}
                style={s}
                generations={generations || []}
                adminPassword={adminPassword}
                apiBase={apiBase}
                onChanged={onStyleChanged}
                onDeleted={onStyleDeleted}
                canDelete={s.id !== "minimalist_default"}
                onMoveUp={idx === 0 ? null : () => moveStyle(idx, -1)}
                onMoveDown={idx === arr.length - 1 ? null : () => moveStyle(idx, 1)}
              />
            ))}
        </div>
      )}
    </div>
  );
};

/* ---- Style Variants Editor ---- */
/* Shared editor for the list of variants attached to any Style. Each variant
   produces one portrait shown to the customer. Customers never see variant
   names — they only see the rendered portraits. */
/* ---- Style Section ---- */
/* A single Style row inside Renaissance/IPL/Modern admin panels.
   Combines the style metadata controls (thumbnail picker, coming-soon flag,
   delete) with the prompt variants editor. */
const StyleSection = ({ style, generations, adminPassword, apiBase, onChanged, onDeleted, canDelete = true, onMoveUp, onMoveDown }) => {
  const headers = { "X-Admin-Password": adminPassword };
  const [pickerMode, setPickerMode] = useState(null); // 'thumb' | 'coming_soon'
  const [saving, setSaving] = useState(false);

  const patchStyle = async (patch) => {
    try {
      setSaving(true);
      const res = await axios.patch(
        `${apiBase}/admin/styles/${style.id}`,
        patch,
        { headers }
      );
      toast.success("Saved");
      onChanged && onChanged(res.data.style);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onPickImage = (url) => {
    const field = pickerMode === "coming_soon" ? "coming_soon_image_url" : "thumbnail_url";
    setPickerMode(null);
    patchStyle({ [field]: url });
  };

  const deleteStyle = async () => {
    if (!window.confirm(`Delete "${style.name}"? This removes the style and all its prompts. This cannot be undone.`)) return;
    try {
      await axios.delete(`${apiBase}/admin/styles/${style.id}`, { headers });
      toast.success("Style deleted");
      onDeleted && onDeleted(style.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <section
      className="border border-border/60 bg-background/40 p-4 sm:p-6"
      data-testid={`style-section-${style.id}`}
    >
      <div className="flex items-start gap-4 flex-wrap mb-4">
        <div className="w-20 h-28 flex-shrink-0 border border-border/40 overflow-hidden bg-muted/30">
          {style.thumbnail_url ? (
            <img src={style.thumbnail_url} alt={style.name} className="w-full h-full object-cover" />
          ) : null}
        </div>
        <div className="flex-1 min-w-[200px]">
          {/* Inline-edit style name. Save-on-blur; Enter also saves. */}
          <input
            type="text"
            defaultValue={style.name}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next && next !== style.name) {
                patchStyle({ name: next });
              } else if (!next) {
                e.target.value = style.name; // revert empty input
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                e.currentTarget.value = style.name;
                e.currentTarget.blur();
              }
            }}
            className="text-xl font-bold mb-1 bg-transparent w-full outline-none border-0 border-b border-transparent hover:border-border/60 focus:border-primary focus:bg-card transition-colors px-1 -mx-1"
            data-testid={`style-name-input-${style.id}`}
            disabled={saving}
            aria-label="Style name"
          />
          <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground mb-2">
            <span>ID: <code className="text-[10px]">{style.id}</code></span>
            <label className="flex items-center gap-1 cursor-pointer select-none text-foreground">
              <input
                type="checkbox"
                checked={!!style.coming_soon}
                disabled={saving}
                onChange={(e) => patchStyle({ coming_soon: e.target.checked })}
                data-testid={`style-${style.id}-coming-soon`}
              />
              Coming soon
            </label>
            <label className="flex items-center gap-1 cursor-pointer select-none text-foreground">
              <input
                type="checkbox"
                checked={style.active !== false}
                disabled={saving}
                onChange={(e) => patchStyle({ active: e.target.checked })}
                data-testid={`style-${style.id}-active`}
              />
              Active
            </label>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1 mr-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={onMoveUp || undefined}
              disabled={!onMoveUp}
              className="h-7 w-7 p-0"
              aria-label="Move up"
              data-testid={`style-${style.id}-up`}
            >
              ↑
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onMoveDown || undefined}
              disabled={!onMoveDown}
              className="h-7 w-7 p-0"
              aria-label="Move down"
              data-testid={`style-${style.id}-down`}
            >
              ↓
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPickerMode("thumb")}
            data-testid={`style-${style.id}-pick-thumb`}
          >
            Pick thumbnail
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPickerMode("coming_soon")}
            data-testid={`style-${style.id}-pick-coming-soon`}
          >
            Coming soon image
          </Button>
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-700 hover:text-red-800"
              onClick={deleteStyle}
              data-testid={`style-${style.id}-delete`}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {style.coming_soon && (
        <div className="text-xs text-muted-foreground mb-3">
          Coming-soon image: {style.coming_soon_image_url ? (
            <a href={style.coming_soon_image_url} target="_blank" rel="noreferrer" className="text-primary underline">
              view
            </a>
          ) : "not set — will use the regular thumbnail if available."}
        </div>
      )}

      <StyleVariantsEditor
        styleId={style.id}
        collectionId={style.collection_id}
        adminPassword={adminPassword}
        apiBase={apiBase}
        generations={generations}
      />

      {pickerMode && (
        <ImagePickerModal
          slot={pickerMode}
          slotLabel={pickerMode === "coming_soon" ? "Coming-soon image" : `${style.name} thumbnail`}
          slotHint={
            pickerMode === "coming_soon"
              ? "Shown on the homepage card when this style is marked Coming Soon. Upload from your computer, pick any generated image, or paste a URL."
              : "Card image shown on the homepage. Upload from your computer, pick a generated image (raw or framed), or paste a URL."
          }
          mode="raw"
          generations={generations}
          allowUpload={true}
          adminPassword={adminPassword}
          apiBase={apiBase}
          onClose={() => setPickerMode(null)}
          onSelect={onPickImage}
        />
      )}
    </section>
  );
};

const StyleVariantsEditor = ({ styleId, collectionId, adminPassword, apiBase, generations }) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pickerVariantId, setPickerVariantId] = useState(null); // which variant is choosing a reference image
  const headers = { "X-Admin-Password": adminPassword };
  const supportsReference = collectionId === "ipl";

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/styles/${styleId}/variants`, { headers });
      setVariants(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load variants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleId]);

  const updateLocal = (id, key, value) => {
    setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, [key]: value } : v)));
  };

  // Per-variant size tab state. `a4` is the default to mirror the legacy single-prompt UX.
  const [promptSizeTab, setPromptSizeTab] = useState({}); // {variantId: 'a4'|'8x8'|'8x10'|'10x10'}
  const setSizeTab = (vid, sz) =>
    setPromptSizeTab((p) => ({ ...p, [vid]: sz }));

  const updateSizedPrompt = (vid, sizeKey, value) => {
    setVariants((prev) =>
      prev.map((v) =>
        v.id === vid
          ? {
              ...v,
              prompts: { ...(v.prompts || {}), [sizeKey]: value },
            }
          : v
      )
    );
  };

  const saveVariant = async (v) => {
    try {
      setSavingId(v.id);
      // Sync the per-size prompts: ensure all 4 keys are present (fall back
      // to v.prompt for any size that wasn't edited yet — keeps legacy flows
      // working). The backend merges by key, so we only send the keys we
      // actually want to set.
      const sizedPrompts = {};
      SIZE_KEYS.forEach((sz) => {
        const val = (v.prompts && v.prompts[sz]) || v.prompt || "";
        if (val && val.trim()) sizedPrompts[sz] = val;
      });
      const res = await axios.patch(
        `${apiBase}/admin/style-variants/${v.id}`,
        {
          name: v.name,
          prompt: v.prompt,
          prompts: sizedPrompts,
          active: v.active,
          sort_order: v.sort_order,
          model: v.model || "nano_banana",
        },
        { headers }
      );
      setVariants((prev) => prev.map((x) => (x.id === v.id ? res.data.variant : x)));
      toast.success(`${res.data.variant.name} saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save variant");
    } finally {
      setSavingId(null);
    }
  };

  const patchVariantField = async (vid, patch, successMsg) => {
    try {
      setSavingId(vid);
      const res = await axios.patch(
        `${apiBase}/admin/style-variants/${vid}`,
        patch,
        { headers }
      );
      setVariants((prev) => prev.map((x) => (x.id === vid ? res.data.variant : x)));
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const setReferenceImage = (vid, url) => {
    setPickerVariantId(null);
    patchVariantField(vid, { base_image_url: url }, "Reference image saved");
  };

  const clearReferenceImage = (vid) => {
    if (!window.confirm("Remove the reference image for this variant?")) return;
    patchVariantField(vid, { base_image_url: "" }, "Reference image cleared");
  };

  const addVariant = async () => {
    try {
      setAdding(true);
      const nextLetter = String.fromCharCode(65 + variants.length);
      const res = await axios.post(
        `${apiBase}/admin/styles/${styleId}/variants`,
        { name: `Variant ${nextLetter}`, prompt: "Describe the variant prompt here...", active: true },
        { headers }
      );
      setVariants((prev) => [...prev, res.data.variant]);
      toast.success(`Added ${res.data.variant.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add variant");
    } finally {
      setAdding(false);
    }
  };

  const deleteVariant = async (id) => {
    if (!window.confirm("Delete this variant? This change is immediate.")) return;
    try {
      await axios.delete(`${apiBase}/admin/style-variants/${id}`, { headers });
      setVariants((prev) => prev.filter((v) => v.id !== id));
      toast.success("Variant deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete variant");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid={`variants-editor-${styleId}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-bold text-lg">Variants</h4>
          <p className="text-xs text-muted-foreground">
            Each active variant is one Gemini call and produces one portrait shown to the customer.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={addVariant}
          disabled={adding || variants.length >= 8}
          data-testid={`variants-add-${styleId}`}
        >
          {adding ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : "+ Add variant"}
        </Button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-6 border border-dashed border-border/60">
          No variants yet. Add at least one to enable generation.
        </div>
      ) : (
        <div className="space-y-4">
          {variants.map((v) => (
            <div
              key={v.id}
              className="border border-border/60 p-3 sm:p-4 bg-background/60"
              data-testid={`variant-row-${v.id}`}
            >
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <Input
                  value={v.name || ""}
                  onChange={(e) => updateLocal(v.id, "name", e.target.value)}
                  className="max-w-xs font-bold"
                  placeholder="Variant name"
                  data-testid={`variant-${v.id}-name`}
                />
                <label className="text-xs flex items-center gap-1 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={v.active !== false}
                    onChange={(e) => updateLocal(v.id, "active", e.target.checked)}
                    data-testid={`variant-${v.id}-active`}
                  />
                  Active
                </label>
                <label className="text-xs flex items-center gap-2 select-none">
                  <span className="text-muted-foreground">Model</span>
                  <select
                    value={v.model || "nano_banana"}
                    onChange={(e) => updateLocal(v.id, "model", e.target.value)}
                    className="text-xs bg-background border border-border/60 rounded px-2 py-1 focus:outline-none focus:border-primary"
                    data-testid={`variant-${v.id}-model`}
                  >
                    <option value="nano_banana">Nano Banana (Gemini)</option>
                    <option value="gpt_image_1">GPT Image 1 (OpenAI)</option>
                  </select>
                </label>
                <span className="text-xs text-muted-foreground">
                  Order: {v.sort_order}
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => saveVariant(v)}
                    disabled={savingId === v.id}
                    data-testid={`variant-${v.id}-save`}
                  >
                    {savingId === v.id ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Saving
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteVariant(v.id)}
                    className="text-red-700 hover:text-red-800"
                    data-testid={`variant-${v.id}-delete`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div className="border-t border-border/40 pt-3 mb-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="label-uppercase text-[10px]">Prompt for</span>
                  {SIZE_KEYS.map((sz) => {
                    const active = (promptSizeTab[v.id] || "a4") === sz;
                    const hasValue = !!(v.prompts && v.prompts[sz] && v.prompts[sz].trim());
                    return (
                      <button
                        key={sz}
                        type="button"
                        onClick={() => setSizeTab(v.id, sz)}
                        className={`px-2.5 py-1 text-xs border ${
                          active
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border/60 text-muted-foreground hover:border-primary/40"
                        }`}
                        data-testid={`variant-${v.id}-prompt-tab-${sz}`}
                      >
                        {SIZE_KEY_LABELS[sz] || sz}
                        {hasValue ? "" : " *"}
                      </button>
                    );
                  })}
                  <span className="text-[10px] text-muted-foreground ml-2">
                    * = empty (will fall back to legacy prompt)
                  </span>
                </div>
              </div>
              {(() => {
                const curSize = promptSizeTab[v.id] || "a4";
                const value =
                  (v.prompts && v.prompts[curSize] !== undefined
                    ? v.prompts[curSize]
                    : v.prompt) || "";
                return (
                  <textarea
                    rows={10}
                    value={value}
                    onChange={(e) => {
                      // Always write to the prompts map. Also mirror into
                      // `prompt` when editing the A4 tab so the legacy
                      // single-prompt field stays a reasonable fallback.
                      updateSizedPrompt(v.id, curSize, e.target.value);
                      if (curSize === "a4") updateLocal(v.id, "prompt", e.target.value);
                    }}
                    className="w-full font-mono text-xs bg-background border border-border/60 p-3 focus:outline-none focus:border-primary resize-y"
                    data-testid={`variant-${v.id}-prompt`}
                  />
                );
              })()}
              {supportsReference && (
                <div className="mt-3 border-t border-border/40 pt-3 flex items-start gap-3 flex-wrap">
                  <div className="w-20 h-20 flex-shrink-0 border border-border/60 bg-muted/30 overflow-hidden">
                    {v.base_image_url ? (
                      <img
                        src={v.base_image_url}
                        alt="Reference"
                        className="w-full h-full object-contain"
                        data-testid={`variant-${v.id}-ref-thumb`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">
                        No ref image
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-xs font-bold mb-1">Reference jersey image</div>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Sent to Nano Banana alongside the pet photo so the AI matches the exact jersey design. Leave blank to generate from prompt only.
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPickerVariantId(v.id)}
                      disabled={savingId === v.id}
                      data-testid={`variant-${v.id}-ref-pick`}
                    >
                      {v.base_image_url ? "Replace image" : "Set image"}
                    </Button>
                    {v.base_image_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => clearReferenceImage(v.id)}
                        disabled={savingId === v.id}
                        className="text-red-700 hover:text-red-800"
                        data-testid={`variant-${v.id}-ref-clear`}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {pickerVariantId && (
        <ImagePickerModal
          slot="reference"
          slotLabel="Reference image"
          slotHint="Upload the team jersey (PNG/JPG). It's sent to Nano Banana alongside the customer's pet photo so the AI nails the exact jersey design."
          mode="raw"
          generations={generations || []}
          adminPassword={adminPassword}
          apiBase={apiBase}
          allowUpload={true}
          onClose={() => setPickerVariantId(null)}
          onSelect={(url) => setReferenceImage(pickerVariantId, url)}
        />
      )}
    </div>
  );
};

/* ---- Sessions Tab (Event Stream Analytics) ---- */
const SessionsDownloadButton = ({ adminPassword, apiBase, includeAdmin }) => {
  const [days, setDays] = useState(30);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(`${apiBase}/admin/sessions/export`, {
        headers: { "X-Admin-Password": adminPassword },
        params: { include_admin: includeAdmin ? "true" : "false", days },
        responseType: "blob",
      });
      const rowCount = res.headers["x-row-count"] || "?";
      // Pull filename out of Content-Disposition so the saved file matches
      // the backend's timestamp suffix.
      let filename = `sessions_${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m && m[1]) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rowCount} events`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={days}
        onChange={(e) => setDays(parseInt(e.target.value, 10))}
        disabled={downloading}
        className="border border-border/60 bg-card text-xs px-2 py-1.5 h-8"
        data-testid="sessions-export-days"
      >
        <option value="1">Last 24h</option>
        <option value="7">Last 7d</option>
        <option value="30">Last 30d</option>
        <option value="90">Last 90d</option>
        <option value="365">Last year</option>
      </select>
      <Button
        size="sm"
        variant="outline"
        onClick={download}
        disabled={downloading}
        data-testid="sessions-export-button"
      >
        {downloading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            Exporting…
          </>
        ) : (
          "Download CSV"
        )}
      </Button>
    </div>
  );
};

const SessionsPanel = ({ adminPassword, apiBase }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeAdmin, setIncludeAdmin] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const headers = { "X-Admin-Password": adminPassword };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/sessions`, {
        headers,
        params: { include_admin: includeAdmin ? "true" : "false", limit: 200 },
      });
      setSessions(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAdmin]);

  const fmtDuration = (a, b) => {
    if (!a || !b) return "—";
    const diff = (new Date(b).getTime() - new Date(a).getTime()) / 1000;
    if (diff < 60) return `${Math.max(0, Math.round(diff))}s`;
    if (diff < 3600) return `${Math.round(diff / 60)}m`;
    return `${(diff / 3600).toFixed(1)}h`;
  };

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="sessions-panel">
      <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">User Sessions</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-xs flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeAdmin}
              onChange={(e) => setIncludeAdmin(e.target.checked)}
              data-testid="sessions-include-admin"
            />
            Include admin sessions (URL ?admin=1)
          </label>
          <SessionsDownloadButton
            adminPassword={adminPassword}
            apiBase={apiBase}
            includeAdmin={includeAdmin}
          />
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        One row per browser/session. Click a row to see the full chronological
        event stream (page views, clicks, scroll milestones, section
        impressions). Admin/staff sessions are excluded by default.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-center text-muted-foreground py-10">No sessions yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 px-2 label-uppercase">Session</th>
                <th className="py-3 px-2 label-uppercase">Landing</th>
                <th className="py-3 px-2 label-uppercase">Exit</th>
                <th className="py-3 px-2 label-uppercase">Events</th>
                <th className="py-3 px-2 label-uppercase">Duration</th>
                <th className="py-3 px-2 label-uppercase">Last Seen (IST)</th>
                <th className="py-3 px-2 label-uppercase">Referrer</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.session_id}
                  className="border-b border-border/60 cursor-pointer hover:bg-muted/30"
                  onClick={() => setActiveSession(s)}
                  data-testid={`session-row-${s.session_id}`}
                >
                  <td className="py-3 px-2 font-mono text-[11px]">
                    {(s.session_id || "").slice(0, 10)}…
                    {s.is_admin && (
                      <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        ADMIN
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 font-mono text-[11px]">
                    {s.first_path || "—"}
                  </td>
                  <td className="py-3 px-2 font-mono text-[11px]">
                    {s.last_path || "—"}
                  </td>
                  <td className="py-3 px-2">{s.total_events}</td>
                  <td className="py-3 px-2">
                    {fmtDuration(s.first_seen, s.last_seen)}
                  </td>
                  <td className="py-3 px-2 text-xs">
                    {fmtIST(s.last_seen)}
                  </td>
                  <td className="py-3 px-2 text-xs truncate max-w-[200px]">
                    {s.referrer ? new URL(s.referrer).hostname : "direct"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeSession && (
        <SessionDetailModal
          session={activeSession}
          adminPassword={adminPassword}
          apiBase={apiBase}
          onClose={() => setActiveSession(null)}
        />
      )}
    </div>
  );
};

const SessionDetailModal = ({ session, adminPassword, apiBase, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios
      .get(`${apiBase}/admin/sessions/${session.session_id}/events`, {
        headers: { "X-Admin-Password": adminPassword },
      })
      .then((res) => {
        if (cancel) return;
        setEvents(res.data.items || []);
      })
      .catch(() => {
        if (!cancel) setEvents([]);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [session.session_id, adminPassword, apiBase]);

  // Derive a per-user funnel from the chronologically sorted event stream.
  // Stages are detected by event shape (no extra backend query needed):
  //  - landed       : at least one pageview
  //  - style_picked : click on a hub style tile (data-track starts with "hub_pick_")
  //  - upload       : pageview hitting /upload
  //  - generated    : generation_started event
  //  - checkout     : pageview hitting /checkout
  //  - paid         : pageview hitting /success (only loads after Razorpay confirms)
  const journey = useMemo(() => {
    const stages = [
      { key: "landed",       label: "Landed",      reached: false, at: null },
      { key: "style_picked", label: "Picked style",reached: false, at: null, detail: null },
      { key: "upload",       label: "Upload page", reached: false, at: null },
      { key: "generated",    label: "Generated",   reached: false, at: null },
      { key: "checkout",     label: "Checkout",    reached: false, at: null },
      { key: "paid",         label: "Paid",        reached: false, at: null },
    ];
    const set = (key, at, detail) => {
      const s = stages.find((x) => x.key === key);
      if (s && !s.reached) {
        s.reached = true;
        s.at = at;
        if (detail !== undefined) s.detail = detail;
      }
    };
    for (const e of events) {
      const t = e.event_type;
      const path = e.path || "";
      const md = e.metadata || {};
      if (t === "pageview") {
        set("landed", e.created_at);
        if (path.startsWith("/upload")) set("upload", e.created_at);
        if (path.startsWith("/checkout")) set("checkout", e.created_at);
        if (path.startsWith("/success")) set("paid", e.created_at);
      } else if (t === "click") {
        const track = md.track || "";
        if (track.startsWith("hub_pick_")) {
          set(
            "style_picked",
            e.created_at,
            track.replace("hub_pick_", "")
          );
        }
      } else if (t === "generation_started") {
        set("generated", e.created_at);
      }
    }
    // Identify the last-reached stage and the next (drop-off) stage
    let lastReachedIdx = -1;
    stages.forEach((s, i) => {
      if (s.reached) lastReachedIdx = i;
    });
    const droppedAt =
      lastReachedIdx >= 0 && lastReachedIdx < stages.length - 1
        ? stages[lastReachedIdx + 1]
        : null;
    return { stages, lastReachedIdx, droppedAt };
  }, [events]);

  const eventBadge = (t) => {
    const map = {
      pageview: "bg-blue-100 text-blue-800",
      click: "bg-emerald-100 text-emerald-800",
      session_end: "bg-rose-100 text-rose-800",
      generation_started: "bg-purple-100 text-purple-800",
      section_view: "bg-amber-100 text-amber-800",
    };
    if (t && t.startsWith("scroll_")) return "bg-slate-100 text-slate-700";
    return map[t] || "bg-slate-100 text-slate-700";
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="session-detail-modal"
    >
      <div
        className="bg-background border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Session Stream</h3>
            <p className="text-xs font-mono text-muted-foreground mt-1 truncate">
              {session.session_id}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {fmtIST(session.first_seen)} → {fmtIST(session.last_seen)} ·{" "}
              {session.total_events} events
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="session-detail-close">
            Close
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!loading && events.length > 0 && (
            <SessionJourney journey={journey} />
          )}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">No events.</p>
          ) : (
            <table className="w-full text-sm" data-testid="session-events-table">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b border-border text-left">
                  <th className="py-2 px-3 label-uppercase">Time (IST)</th>
                  <th className="py-2 px-3 label-uppercase">Event</th>
                  <th className="py-2 px-3 label-uppercase">Path</th>
                  <th className="py-2 px-3 label-uppercase">Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const md = e.metadata || {};
                  const detail =
                    e.event_type === "click"
                      ? [md.testid, md.track, md.label].filter(Boolean).join(" · ")
                      : Object.entries(md)
                          .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
                          .join(" · ");
                  return (
                    <tr key={i} className="border-b border-border/40">
                      <td className="py-2 px-3 text-xs whitespace-nowrap">
                        {fmtIST(e.created_at, { dateStyle: undefined, timeStyle: "medium" })}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-block text-[10px] px-2 py-0.5 rounded font-medium ${eventBadge(e.event_type)}`}
                        >
                          {e.event_type}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px]">{e.path}</td>
                      <td className="py-2 px-3 text-xs">{detail || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---- Per-session journey strip rendered inside SessionDetailModal ---- */
const SessionJourney = ({ journey }) => {
  const { stages, lastReachedIdx, droppedAt } = journey;
  const reachedCount = stages.filter((s) => s.reached).length;
  const overallPct = Math.round((reachedCount / stages.length) * 100);
  return (
    <div
      className="px-5 pt-5 pb-4 border-b border-border bg-muted/20"
      data-testid="session-journey"
    >
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-xs label-uppercase text-muted-foreground">
            Per-user funnel
          </p>
          <h4 className="font-bold text-sm mt-0.5">
            Reached {reachedCount} of {stages.length} stages
            <span className="text-muted-foreground font-normal ml-2">
              ({overallPct}%)
            </span>
          </h4>
        </div>
        {droppedAt && (
          <span className="text-xs bg-red-50 text-red-800 px-2 py-1 rounded">
            Dropped before <strong>{droppedAt.label}</strong>
          </span>
        )}
        {!droppedAt && reachedCount === stages.length && (
          <span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-1 rounded">
            Completed full funnel
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {stages.map((s, i) => {
          const reached = s.reached;
          const isDropPoint =
            !reached &&
            i === lastReachedIdx + 1 &&
            lastReachedIdx < stages.length - 1;
          return (
            <div
              key={s.key}
              data-testid={`session-journey-${s.key}`}
              className={`relative px-2 py-2 rounded text-xs border ${
                reached
                  ? i === stages.length - 1
                    ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                    : "bg-primary/10 border-primary/30 text-foreground"
                  : isDropPoint
                  ? "bg-red-50 border-red-300 text-red-900"
                  : "bg-card border-border/60 text-muted-foreground"
              }`}
            >
              <div className="flex items-baseline justify-between gap-1">
                <span className="font-mono text-[10px] opacity-60">
                  {i + 1}
                </span>
                <span className="text-[10px]">
                  {reached ? "✓" : isDropPoint ? "✕" : "—"}
                </span>
              </div>
              <div className="font-medium mt-1 leading-tight">{s.label}</div>
              {s.detail && (
                <div className="text-[10px] mt-0.5 truncate" title={s.detail}>
                  {s.detail}
                </div>
              )}
              {reached && s.at && (
                <div className="text-[10px] mt-1 opacity-60">
                  {fmtIST(s.at, { dateStyle: undefined, timeStyle: "short" })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---- Testimonials Tab ---- */
const TestimonialsPanel = ({ adminPassword, apiBase, generations }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    image_url: "",
    text: "",
    customer_name: "",
    customer_location: "",
    pet_name: "",
    rating: 5,
  });
  const [picker, setPicker] = useState(null); // null | {mode:'new'} | {mode:'edit', id}
  const [fieldDrafts, setFieldDrafts] = useState({}); // id -> {field: pending}
  const headers = { "X-Admin-Password": adminPassword };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/testimonials`, { headers });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTestimonial = async () => {
    if (!draft.image_url || !draft.text.trim()) {
      toast.error("Image and testimonial text are required");
      return;
    }
    try {
      setCreating(true);
      const res = await axios.post(
        `${apiBase}/admin/testimonials`,
        draft,
        { headers }
      );
      setItems((p) => [...p, res.data.testimonial]);
      setDraft({
        image_url: "",
        text: "",
        customer_name: "",
        customer_location: "",
        pet_name: "",
        rating: 5,
      });
      toast.success("Testimonial added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const patchTestimonial = async (id, payload, msg) => {
    try {
      const res = await axios.patch(
        `${apiBase}/admin/testimonials/${id}`,
        payload,
        { headers }
      );
      setItems((p) =>
        p.map((t) => (t.id === id ? res.data.testimonial : t))
      );
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${apiBase}/admin/testimonials/${id}`, { headers });
      setItems((p) => p.filter((t) => t.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const sorted = [...items];
    [sorted[idx], sorted[target]] = [sorted[target], sorted[idx]];
    setItems(sorted);
    try {
      await axios.post(
        `${apiBase}/admin/testimonials/reorder`,
        { ids: sorted.map((t) => t.id) },
        { headers }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reorder failed");
      load();
    }
  };

  const setFieldDraft = (id, field, value) =>
    setFieldDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));

  const commitField = (id, field, original) => {
    const draftVal = fieldDrafts[id]?.[field];
    if (draftVal === undefined || draftVal === original) return;
    setFieldDrafts((p) => {
      const next = { ...p, [id]: { ...(p[id] || {}) } };
      delete next[id][field];
      return next;
    });
    patchTestimonial(id, { [field]: draftVal }, "Saved");
  };

  return (
    <div className="space-y-6" data-testid="testimonials-panel">
      <div className="bg-card border border-border/60 p-6">
        <h2 className="text-2xl font-bold mb-2">Testimonials</h2>
        <p className="text-sm text-muted-foreground">
          Real customer photos + text shown on the homepage (after “How it
          works”) and on every /promo/* landing page. Section auto-switches
          from a 2-column grid to a horizontal carousel when you have 3 or
          more.
        </p>
      </div>

      {/* New testimonial form */}
      <div className="bg-card border border-border/60 p-6" data-testid="testimonial-new">
        <h3 className="font-bold mb-4">Add a testimonial</h3>
        <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-5">
          <div>
            <Label className="label-uppercase mb-1.5 block">Photo</Label>
            <div className="w-full aspect-square border border-border/60 bg-muted/30 overflow-hidden mb-2">
              {draft.image_url ? (
                <img src={draft.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground text-center px-2">
                  Pick or upload a photo
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPicker({ mode: "new" })}
              className="w-full"
              data-testid="testimonial-new-pick"
            >
              {draft.image_url ? "Replace" : "Pick image"}
            </Button>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Testimonial text *</Label>
              <textarea
                rows={4}
                value={draft.text}
                onChange={(e) => setDraft((p) => ({ ...p, text: e.target.value }))}
                className="w-full bg-background border border-border/60 p-3 text-sm focus:outline-none focus:border-primary resize-y"
                placeholder="The frame looks beautiful in our living room. Buddy goes nuts every time he walks past it..."
                data-testid="testimonial-new-text"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Customer name</Label>
                <Input
                  value={draft.customer_name}
                  onChange={(e) => setDraft((p) => ({ ...p, customer_name: e.target.value }))}
                  placeholder="e.g. Priya"
                  data-testid="testimonial-new-name"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Location</Label>
                <Input
                  value={draft.customer_location}
                  onChange={(e) => setDraft((p) => ({ ...p, customer_location: e.target.value }))}
                  placeholder="e.g. Bengaluru"
                  data-testid="testimonial-new-location"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Pet name</Label>
                <Input
                  value={draft.pet_name}
                  onChange={(e) => setDraft((p) => ({ ...p, pet_name: e.target.value }))}
                  placeholder="e.g. Buddy"
                  data-testid="testimonial-new-pet"
                />
              </div>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Rating</Label>
              <StarPicker
                value={draft.rating}
                onChange={(v) => setDraft((p) => ({ ...p, rating: v }))}
              />
            </div>
            <Button
              onClick={createTestimonial}
              disabled={creating || !draft.image_url || !draft.text.trim()}
              className="mt-2"
              data-testid="testimonial-new-submit"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Add testimonial
            </Button>
          </div>
        </div>
      </div>

      {/* Existing testimonials */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 bg-card border border-border/60">
            No testimonials yet. Add one above.
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((t, idx) => (
              <div
                key={t.id}
                className="bg-card border border-border/60 p-4 flex flex-col sm:flex-row gap-4"
                data-testid={`testimonial-row-${t.id}`}
              >
                <div className="flex sm:flex-col items-start gap-2 sm:w-32 flex-shrink-0">
                  <div className="w-24 sm:w-full aspect-square border border-border/60 overflow-hidden bg-muted/30">
                    {t.image_url && (
                      <img src={t.image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPicker({ mode: "edit", id: t.id })}
                    className="w-full text-xs h-7"
                    data-testid={`testimonial-${t.id}-replace`}
                  >
                    Replace image
                  </Button>
                </div>
                <div className="flex-1 space-y-2">
                  <textarea
                    rows={3}
                    value={
                      fieldDrafts[t.id]?.text !== undefined
                        ? fieldDrafts[t.id].text
                        : t.text || ""
                    }
                    onChange={(e) => setFieldDraft(t.id, "text", e.target.value)}
                    onBlur={() => commitField(t.id, "text", t.text)}
                    className="w-full bg-background border border-border/60 p-2 text-sm focus:outline-none focus:border-primary resize-y"
                    data-testid={`testimonial-${t.id}-text`}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {["customer_name", "customer_location", "pet_name"].map((f) => (
                      <Input
                        key={f}
                        value={
                          fieldDrafts[t.id]?.[f] !== undefined
                            ? fieldDrafts[t.id][f]
                            : t[f] || ""
                        }
                        onChange={(e) => setFieldDraft(t.id, f, e.target.value)}
                        onBlur={() => commitField(t.id, f, t[f])}
                        placeholder={
                          f === "customer_name"
                            ? "Customer name"
                            : f === "customer_location"
                            ? "Location"
                            : "Pet name"
                        }
                        className="h-8 text-xs"
                        data-testid={`testimonial-${t.id}-${f.replace("_", "-")}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                    <StarPicker
                      value={t.rating || 5}
                      onChange={(v) => patchTestimonial(t.id, { rating: v }, "Saved")}
                    />
                    <div className="flex items-center gap-3">
                      <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={t.active !== false}
                          onChange={(e) =>
                            patchTestimonial(t.id, { active: e.target.checked }, "Saved")
                          }
                          data-testid={`testimonial-${t.id}-active`}
                        />
                        Active
                      </label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="h-7 w-7 p-0"
                        aria-label="Move up"
                        data-testid={`testimonial-${t.id}-up`}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => move(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="h-7 w-7 p-0"
                        aria-label="Move down"
                        data-testid={`testimonial-${t.id}-down`}
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteTestimonial(t.id)}
                        className="text-red-700 hover:text-red-800 text-xs"
                        data-testid={`testimonial-${t.id}-delete`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {picker && (
        <ImagePickerModal
          slot="testimonial"
          slotLabel="Testimonial photo"
          slotHint="A real photo of the customer with their pet or framed portrait. Upload from your computer for best results."
          mode="raw"
          generations={generations || []}
          adminPassword={adminPassword}
          apiBase={apiBase}
          allowUpload={true}
          onClose={() => setPicker(null)}
          onSelect={(url) => {
            const p = picker;
            setPicker(null);
            if (p.mode === "new") {
              setDraft((d) => ({ ...d, image_url: url }));
            } else if (p.mode === "edit") {
              patchTestimonial(p.id, { image_url: url }, "Image updated");
            }
          }}
        />
      )}
    </div>
  );
};

const StarPicker = ({ value, onChange }) => (
  <div className="inline-flex items-center gap-0.5" data-testid="star-picker">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        aria-label={`${i} star${i > 1 ? "s" : ""}`}
        className="p-0.5"
        data-testid={`star-${i}`}
      >
        <svg
          viewBox="0 0 24 24"
          fill={i <= value ? "#C8A27A" : "none"}
          stroke={i <= value ? "#C8A27A" : "#D9D3CA"}
          strokeWidth="1.5"
          className="w-5 h-5"
        >
          <polygon points="12 2 15 9 22 9.3 17 14 19 22 12 18 5 22 7 14 2 9.3 9 9 12 2" />
        </svg>
      </button>
    ))}
  </div>
);

/* ---- Promo Pages Tab ---- */
const PromoPagesPanel = ({ adminPassword, apiBase, collections, generations }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // slug -> {field: pendingValue}
  const [picker, setPicker] = useState(null); // {slug, field}
  const headers = { "X-Admin-Password": adminPassword };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/admin/promo-pages`, { headers });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load promo pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = async (slug, payload, msg) => {
    try {
      const res = await axios.patch(
        `${apiBase}/admin/promo-pages/${slug}`,
        payload,
        { headers }
      );
      setItems((prev) =>
        prev.map((p) => (p.slug === slug ? res.data.promo : p))
      );
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const setDraft = (slug, field, value) =>
    setDrafts((d) => ({ ...d, [slug]: { ...(d[slug] || {}), [field]: value } }));

  const commit = (slug, field, original) => {
    const draft = drafts[slug]?.[field];
    if (draft === undefined || draft === original) return;
    setDrafts((d) => {
      const next = { ...d, [slug]: { ...(d[slug] || {}) } };
      delete next[slug][field];
      return next;
    });
    patch(slug, { [field]: draft }, "Saved");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-pages-panel">
      <div className="bg-card border border-border/60 p-6">
        <h2 className="text-2xl font-bold mb-2">Promo Pages</h2>
        <p className="text-sm text-muted-foreground">
          Configurable landing pages for paid ad campaigns. Each promo lives at
          <code className="mx-1 px-1 bg-muted/40 rounded">/promo/&lt;slug&gt;</code>
          and auto-applies its coupon at checkout.
        </p>
      </div>

      {items.length === 0 && (
        <p className="text-center text-muted-foreground py-8 bg-card border border-border/60">
          No promo pages yet. The default <code>minimalist</code> promo is
          seeded automatically on backend startup.
        </p>
      )}

      {items.map((p) => {
        const target = (collections || []).find(
          (c) => c.id === p.collection_id
        );
        const styles = []; // we don't have admin/styles wired in here; use a text id input
        return (
          <div
            key={p.slug}
            className="bg-card border border-border/60 p-6"
            data-testid={`promo-row-${p.slug}`}
          >
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <div>
                <h3 className="font-bold text-lg">/promo/{p.slug}</h3>
                <p className="text-xs text-muted-foreground">
                  Collection: <strong>{target?.name || p.collection_id}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/promo/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline text-primary hover:opacity-80"
                  data-testid={`promo-${p.slug}-preview`}
                >
                  Open page ↗
                </a>
                <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={p.active !== false}
                    onChange={(e) =>
                      patch(p.slug, { active: e.target.checked }, "Saved")
                    }
                    data-testid={`promo-${p.slug}-active`}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: text fields */}
              <div className="space-y-4">
                <PromoField label="Headline">
                  <Input
                    value={
                      drafts[p.slug]?.headline !== undefined
                        ? drafts[p.slug].headline
                        : p.headline || ""
                    }
                    onChange={(e) => setDraft(p.slug, "headline", e.target.value)}
                    onBlur={() => commit(p.slug, "headline", p.headline)}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                    data-testid={`promo-${p.slug}-headline`}
                  />
                </PromoField>
                <PromoField label="Subheadline">
                  <Input
                    value={
                      drafts[p.slug]?.subheadline !== undefined
                        ? drafts[p.slug].subheadline
                        : p.subheadline || ""
                    }
                    onChange={(e) =>
                      setDraft(p.slug, "subheadline", e.target.value)
                    }
                    onBlur={() => commit(p.slug, "subheadline", p.subheadline)}
                    data-testid={`promo-${p.slug}-subheadline`}
                  />
                </PromoField>
                <PromoField label="Default Style ID">
                  <Input
                    value={
                      drafts[p.slug]?.default_style_id !== undefined
                        ? drafts[p.slug].default_style_id
                        : p.default_style_id || ""
                    }
                    onChange={(e) =>
                      setDraft(p.slug, "default_style_id", e.target.value)
                    }
                    onBlur={() =>
                      commit(p.slug, "default_style_id", p.default_style_id)
                    }
                    placeholder="e.g. minimalist_stoneleaf_boutique"
                    data-testid={`promo-${p.slug}-default-style`}
                  />
                </PromoField>
                <PromoField label="Coupon Code (auto-applied at checkout)">
                  <Input
                    value={
                      drafts[p.slug]?.coupon_code !== undefined
                        ? drafts[p.slug].coupon_code
                        : p.coupon_code || ""
                    }
                    onChange={(e) =>
                      setDraft(p.slug, "coupon_code", e.target.value.toUpperCase())
                    }
                    onBlur={() => commit(p.slug, "coupon_code", p.coupon_code)}
                    placeholder="e.g. SNUGGLE500"
                    data-testid={`promo-${p.slug}-coupon`}
                  />
                </PromoField>
              </div>

              {/* Right: before image only — right-panel image is now
                  driven by the per-variant gallery (Sizes panel). The
                  legacy `after_image_url` slot has been removed from the UI
                  but the field stays in the DB for rollback safety. */}
              <div className="space-y-4">
                <PromoImageSlot
                  label="Before image (your pet — the user's photo style)"
                  url={p.before_image_url}
                  onPick={() => setPicker({ slug: p.slug, field: "before_image_url" })}
                  onClear={() =>
                    patch(p.slug, { before_image_url: "" }, "Cleared")
                  }
                  testid={`promo-${p.slug}-before`}
                />
              </div>
            </div>
          </div>
        );
      })}

      {picker && (
        <ImagePickerModal
          slot={picker.field}
          slotLabel={picker.field.replace("_image_url", "").replace(/^./, (c) => c.toUpperCase()) + " image"}
          slotHint="This image shows on the /promo/* landing page hero."
          mode="raw"
          generations={generations || []}
          adminPassword={adminPassword}
          apiBase={apiBase}
          allowUpload={true}
          onClose={() => setPicker(null)}
          onSelect={(url) => {
            const slug = picker.slug;
            const field = picker.field;
            setPicker(null);
            patch(slug, { [field]: url }, "Image saved");
          }}
        />
      )}
    </div>
  );
};

const PromoField = ({ label, children }) => (
  <div>
    <Label className="label-uppercase mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const PromoImageSlot = ({ label, url, onPick, onClear, testid }) => (
  <div>
    <Label className="label-uppercase mb-1.5 block">{label}</Label>
    <div className="flex items-center gap-3">
      <div className="w-24 h-32 flex-shrink-0 border border-border/60 bg-muted/30 overflow-hidden">
        {url ? (
          <img
            src={url}
            alt="Preview"
            className="w-full h-full object-cover"
            data-testid={`${testid}-thumb`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">
            Empty
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onPick}
          data-testid={`${testid}-pick`}
        >
          {url ? "Replace" : "Set image"}
        </Button>
        {url && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            className="text-red-700 hover:text-red-800"
            data-testid={`${testid}-clear`}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  </div>
);

/* ---- Collections Tab ---- */
// Product ids supported by the storefront. Mirrors the active rows in
// /api/sizes — kept in sync with the backend seed (DEFAULT_SIZES). Used as
// the columns for the per-collection price matrix and the per-variant prompt
// tabs in the admin UI.
const SIZE_KEYS = ["a4", "8x10", "canvas_8x12", "acrylic_4x6", "mug_11oz"];
const SIZE_KEY_LABELS = {
  a4: "A4",
  "8x10": "8 × 10",
  canvas_8x12: "Canvas 8×12",
  acrylic_4x6: "Acrylic 4×6",
  mug_11oz: "Mug 11oz",
};

const CollectionsPanel = ({ collections, newName, setNewName, creating, onCreate, onUpdate, onReorder, adminPassword, apiBase, generations }) => {
  // Local edit buffers so admin can type freely and save on blur.
  const [priceDrafts, setPriceDrafts] = useState({});
  const [nameDrafts, setNameDrafts] = useState({});
  // Per-size price drafts: { [collectionId]: { a4: "1399", "8x8": "" } }
  const [sizedPriceDrafts, setSizedPriceDrafts] = useState({});
  // Per-collection default coupon code drafts
  const [couponDrafts, setCouponDrafts] = useState({});
  // Thumbnail picker state — { cid } when modal is open for a collection.
  const [thumbPicker, setThumbPicker] = useState(null);

  const sorted = [...collections].sort((a, b) => {
    const ao = a.sort_order ?? 9999;
    const bo = b.sort_order ?? 9999;
    if (ao !== bo) return ao - bo;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });

  const move = (idx, dir) => {
    const next = [...sorted];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onReorder && onReorder(next.map((c) => c.id));
  };

  const commitPrice = (cid) => {
    if (!(cid in priceDrafts)) return;
    const raw = priceDrafts[cid];
    const value = parseFloat(raw);
    setPriceDrafts((p) => {
      const next = { ...p };
      delete next[cid];
      return next;
    });
    if (Number.isNaN(value) || value < 0) {
      toast.error("Enter a valid price (₹) ≥ 0");
      return;
    }
    onUpdate(cid, { price: value });
  };

  const commitName = (cid, original) => {
    if (!(cid in nameDrafts)) return;
    const next = (nameDrafts[cid] || "").trim();
    setNameDrafts((p) => {
      const updated = { ...p };
      delete updated[cid];
      return updated;
    });
    if (!next) {
      toast.error("Collection name can't be empty");
      return;
    }
    if (next === original) return; // unchanged, no PATCH
    onUpdate(cid, { name: next });
  };

  const commitSizedPrice = (cid, sizeKey, currentPrices) => {
    const draft = sizedPriceDrafts[cid]?.[sizeKey];
    if (draft === undefined) return;
    setSizedPriceDrafts((p) => {
      const next = { ...p };
      if (next[cid]) {
        delete next[cid][sizeKey];
        if (Object.keys(next[cid]).length === 0) delete next[cid];
      }
      return next;
    });
    const raw = (draft || "").toString().trim();
    let value = null;
    if (raw !== "") {
      value = parseFloat(raw);
      if (Number.isNaN(value) || value < 0) {
        toast.error("Enter a valid price (₹) ≥ 0");
        return;
      }
    }
    const merged = { ...(currentPrices || {}), [sizeKey]: value };
    onUpdate(cid, { prices: merged });
  };

  const commitCoupon = (cid, original) => {
    if (!(cid in couponDrafts)) return;
    const next = (couponDrafts[cid] || "").trim().toUpperCase();
    setCouponDrafts((p) => {
      const updated = { ...p };
      delete updated[cid];
      return updated;
    });
    const value = next === "" ? null : next;
    if (value === (original || null)) return;
    onUpdate(cid, { default_coupon_code: value });
  };

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="collections-panel">
      <h2 className="text-2xl font-bold mb-2">Collections</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Top-level groupings shown to customers. Reorder them with the arrows — the homepage uses the same order left-to-right. Set the price each collection charges at checkout.
      </p>

      <div className="border border-border/60 p-4 sm:p-6 mb-6">
        <h3 className="font-bold mb-3">Add a new collection</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label htmlFor="new-collection-name" className="label-uppercase mb-2 block">
              Name
            </Label>
            <Input
              id="new-collection-name"
              type="text"
              placeholder="e.g. Pop Art"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={creating}
              data-testid="new-collection-name"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onCreate}
              disabled={creating}
              className="w-full"
              data-testid="new-collection-submit"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add collection"
              )}
            </Button>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 border border-dashed border-border/60">
          No collections yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="collections-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-3 px-4 label-uppercase">Order</th>
                <th className="py-3 px-4 label-uppercase">Name</th>
                <th className="py-3 px-4 label-uppercase">Slug</th>
                <th className="py-3 px-4 label-uppercase">Thumbnail</th>
                <th className="py-3 px-4 label-uppercase">Price (₹)</th>
                <th className="py-3 px-4 label-uppercase">Active</th>
                <th className="py-3 px-4 label-uppercase">Featured</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, idx) => {
                const draft = priceDrafts[c.id];
                const priceValue =
                  draft !== undefined
                    ? draft
                    : c.price !== undefined && c.price !== null
                    ? String(c.price)
                    : "";
                return [
                  <tr
                    key={c.id}
                    className="border-b border-border/60"
                    data-testid={`collection-row-${c.slug}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`collection-${c.slug}-up`}
                          className="h-7 w-7 p-0"
                          aria-label="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => move(idx, 1)}
                          disabled={idx === sorted.length - 1}
                          data-testid={`collection-${c.slug}-down`}
                          className="h-7 w-7 p-0"
                          aria-label="Move down"
                        >
                          ↓
                        </Button>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold">
                      <Input
                        type="text"
                        value={
                          c.id in nameDrafts ? nameDrafts[c.id] : c.name || ""
                        }
                        onChange={(e) =>
                          setNameDrafts((p) => ({ ...p, [c.id]: e.target.value }))
                        }
                        onBlur={() => commitName(c.id, c.name)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") {
                            setNameDrafts((p) => {
                              const next = { ...p };
                              delete next[c.id];
                              return next;
                            });
                            e.target.blur();
                          }
                        }}
                        className="h-8 w-48 font-bold"
                        data-testid={`collection-${c.slug}-name`}
                      />
                    </td>
                    <td className="py-3 px-4 font-mono text-xs">{c.slug}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {c.thumbnail_url ? (
                          <img
                            src={c.thumbnail_url}
                            alt={`${c.name} thumbnail`}
                            className="w-12 h-12 object-cover border border-border/60"
                            data-testid={`collection-${c.slug}-thumb`}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 border border-dashed border-border/60 bg-muted/40 flex items-center justify-center text-[10px] text-muted-foreground"
                            data-testid={`collection-${c.slug}-thumb-empty`}
                          >
                            none
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px] px-2"
                            onClick={() => setThumbPicker({ cid: c.id })}
                            data-testid={`collection-${c.slug}-thumb-pick`}
                          >
                            {c.thumbnail_url ? "Change" : "Pick"}
                          </Button>
                          {c.thumbnail_url && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-[11px] px-2 text-muted-foreground"
                              onClick={() => onUpdate(c.id, { thumbnail_url: "" })}
                              data-testid={`collection-${c.slug}-thumb-clear`}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={priceValue}
                        onChange={(e) =>
                          setPriceDrafts((p) => ({ ...p, [c.id]: e.target.value }))
                        }
                        onBlur={() => commitPrice(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        placeholder="—"
                        className="h-8 w-24"
                        data-testid={`collection-${c.slug}-price`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={c.active !== false}
                          onChange={(e) =>
                            onUpdate(c.id, { active: e.target.checked })
                          }
                          data-testid={`collection-${c.slug}-active`}
                        />
                      </label>
                    </td>
                    <td className="py-3 px-4">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!c.featured}
                          onChange={(e) =>
                            onUpdate(c.id, { featured: e.target.checked })
                          }
                          data-testid={`collection-${c.slug}-featured`}
                        />
                      </label>
                    </td>
                  </tr>,
                  <tr key={`${c.id}-extras`} className="border-b border-border/60 bg-muted/20">
                    <td className="py-3 px-4"></td>
                    <td colSpan={5} className="py-3 px-4">
                      <p className="label-uppercase mb-2">Per-Size Prices (₹)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {SIZE_KEYS.map((sk) => {
                          const draftVal = sizedPriceDrafts[c.id]?.[sk];
                          const existing = (c.prices && c.prices[sk] != null) ? String(c.prices[sk]) : "";
                          const val = draftVal !== undefined ? draftVal : existing;
                          return (
                            <div key={sk}>
                              <Label className="label-uppercase mb-1 block">{SIZE_KEY_LABELS[sk] || sk}</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={val}
                                placeholder="—"
                                onChange={(e) =>
                                  setSizedPriceDrafts((p) => ({
                                    ...p,
                                    [c.id]: { ...(p[c.id] || {}), [sk]: e.target.value },
                                  }))
                                }
                                onBlur={() => commitSizedPrice(c.id, sk, c.prices)}
                                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                className="h-8"
                                data-testid={`collection-${c.slug}-price-${sk}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <p className="label-uppercase mb-1">Default coupon (auto-applied on style page)</p>
                      <Input
                        type="text"
                        placeholder="e.g. SNUGGLE500 — leave blank to disable"
                        value={
                          c.id in couponDrafts
                            ? couponDrafts[c.id]
                            : (c.default_coupon_code || "")
                        }
                        onChange={(e) =>
                          setCouponDrafts((p) => ({ ...p, [c.id]: e.target.value }))
                        }
                        onBlur={() => commitCoupon(c.id, c.default_coupon_code)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className="h-8 w-64 uppercase"
                        data-testid={`collection-${c.slug}-default-coupon`}
                      />
                    </td>
                  </tr>
                ];
              }).flat()}
            </tbody>
          </table>
        </div>
      )}
      {thumbPicker && (
        <ImagePickerModal
          slot="thumbnail_url"
          slotLabel="Collection thumbnail"
          slotHint="Shown next to the collection name on the homepage's Shop by Style row."
          mode="raw"
          generations={generations || []}
          adminPassword={adminPassword}
          apiBase={apiBase}
          allowUpload={true}
          onClose={() => setThumbPicker(null)}
          onSelect={(url) => {
            const cid = thumbPicker.cid;
            setThumbPicker(null);
            onUpdate(cid, { thumbnail_url: url });
          }}
        />
      )}
    </div>
  );
};
// Manages the 4 print sizes (A4, 8x8, 8x10, 10x10). Each size has a Qikink
// SKU, print dimensions, and an optional frame mockup image (with admin-
// editable inner-rect crop coordinates).
/**
 * Reusable single-bucket gallery editor. Owns its own file input and
 * defers upload / delete to props so the parent SizesPanel keeps full
 * control of the auth headers and state-refresh.
 */
const GalleryBucket = ({ label, urls, size, collection, style, uploading, onUpload, onDelete, onMove, testid }) => {
  const upKey = `${size.id}_gallery_${collection}_${style}`;
  const safeUrls = Array.isArray(urls) ? urls : [];
  return (
    <div
      className="border border-border/40 bg-background p-2 space-y-2"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-foreground">{label}</p>
        <span className="text-[10px] text-muted-foreground">
          {safeUrls.length} / 8
        </span>
      </div>
      {safeUrls.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {safeUrls.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="relative aspect-square border border-border/60 bg-card overflow-hidden group"
              data-testid={`${testid}-img-${idx}`}
            >
              <img
                src={url}
                alt={`${label} ${idx + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Reorder + delete controls — visible on hover. The whole
                  control strip lives at the top so it never overlaps the
                  bottom-edge index label or product badging. */}
              <div className="absolute top-0.5 left-0.5 right-0.5 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => onMove && onMove(size, collection, style, idx, idx - 1)}
                    disabled={idx === 0}
                    className="bg-black/70 text-white text-[10px] w-5 h-5 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black"
                    aria-label="Move left"
                    data-testid={`${testid}-up-${idx}`}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove && onMove(size, collection, style, idx, idx + 1)}
                    disabled={idx === safeUrls.length - 1}
                    className="bg-black/70 text-white text-[10px] w-5 h-5 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black"
                    aria-label="Move right"
                    data-testid={`${testid}-down-${idx}`}
                  >
                    ›
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onDelete(size, collection, style, idx)}
                  className="bg-black/70 text-white text-[10px] w-5 h-5 flex items-center justify-center hover:bg-red-700"
                  aria-label="Delete"
                  data-testid={`${testid}-delete-${idx}`}
                >
                  ✕
                </button>
              </div>
              {/* Order index pill at the bottom-left for quick visual
                  confirmation while reordering. */}
              <span className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[9px] px-1 leading-none py-0.5">
                {idx + 1}
              </span>
            </div>
          ))}
        </div>
      )}
      <label htmlFor={`gallery-up-${upKey}`} className="inline-flex">
        <input
          type="file"
          id={`gallery-up-${upKey}`}
          accept="image/*"
          className="hidden"
          onChange={(e) => onUpload(size, collection, style, e.target.files?.[0])}
          disabled={!!uploading[upKey] || safeUrls.length >= 8}
          data-testid={`${testid}-input`}
        />
        <Button
          asChild
          variant="outline"
          size="sm"
          disabled={!!uploading[upKey] || safeUrls.length >= 8}
        >
          <span>
            {uploading[upKey] ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Uploading…
              </>
            ) : safeUrls.length >= 8 ? (
              "Max 8 reached"
            ) : (
              `+ Add image (${safeUrls.length}/8)`
            )}
          </span>
        </Button>
      </label>
    </div>
  );
};

const SizesPanel = ({ adminPassword, apiBase }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // {id: bool}
  const [drafts, setDrafts] = useState({}); // {id: {field: value}}
  const [uploading, setUploading] = useState({}); // {id: bool}
  const [defaultNbPrompt, setDefaultNbPrompt] = useState("");
  const [promptTests, setPromptTests] = useState({}); // {id: {loading, url, mockupIndex}}
  const [collections, setCollections] = useState([]); // [{id, name}]

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/admin/sizes`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setItems(res.data.items || []);
      setDrafts({});
    } catch (e) {
      toast.error("Failed to load sizes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    axios
      .get(`${apiBase}/admin/nano-banana-default-prompt`, {
        headers: { "X-Admin-Password": adminPassword },
      })
      .then((res) => setDefaultNbPrompt(res.data?.prompt || ""))
      .catch(() => {});
    // Style catalog → drives the gallery sub-sections under each variant
    // (Common + per-collection + per-style). Public endpoint, no auth.
    axios
      .get(`${apiBase}/styles/catalog`)
      .then((res) =>
        setCollections(
          (res.data?.collections || []).map((c) => ({
            id: c.id,
            name: c.name,
            // Style list, dropping legacy "coming soon" entries since we
            // can't upload assets for styles customers can't pick anyway.
            styles: (c.styles || [])
              .filter((s) => !s.coming_soon)
              .map((s) => ({ id: s.id, name: s.name })),
          })),
        ),
      )
      .catch(() => setCollections([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftFor = (id, field, fallback) => {
    const d = drafts[id] || {};
    return d[field] !== undefined ? d[field] : fallback;
  };

  const setDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const save = async (sz) => {
    const draft = drafts[sz.id] || {};
    if (Object.keys(draft).length === 0) {
      toast("No changes");
      return;
    }
    setSaving((s) => ({ ...s, [sz.id]: true }));
    try {
      const payload = { ...draft };
      if (payload.frame_inner_str !== undefined) {
        // Parse "left,top,right,bottom" CSV
        const parts = String(payload.frame_inner_str).split(",").map((s) => parseInt(s.trim(), 10));
        if (parts.length === 4 && parts.every((n) => !isNaN(n))) {
          payload.frame_inner = { left: parts[0], top: parts[1], right: parts[2], bottom: parts[3] };
        }
        delete payload.frame_inner_str;
      }
      await axios.patch(`${apiBase}/admin/sizes/${sz.id}`, payload, {
        headers: { "X-Admin-Password": adminPassword },
      });
      toast.success(`${sz.name} saved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [sz.id]: false }));
    }
  };

  const runPromptTest = async (sz) => {
    const mockupCount = (sz.mockups || []).length;
    if (mockupCount === 0) {
      toast.error("Upload at least one mockup first");
      return;
    }
    const draftPrompt = drafts[sz.id]?.nano_banana_prompt;
    const currentPrompt =
      draftPrompt !== undefined ? draftPrompt : (sz.nano_banana_prompt || "");
    const mockupIndex = promptTests[sz.id]?.mockupIndex || 0;
    setPromptTests((p) => ({
      ...p,
      [sz.id]: { ...(p[sz.id] || {}), loading: true, mockupIndex },
    }));
    try {
      const res = await axios.post(
        `${apiBase}/admin/sizes/${sz.id}/test-nano-banana-prompt`,
        { prompt: currentPrompt, mockup_index: mockupIndex },
        {
          headers: { "X-Admin-Password": adminPassword },
          timeout: 60000,
        },
      );
      setPromptTests((p) => ({
        ...p,
        [sz.id]: {
          loading: false,
          url: res.data?.preview_url || null,
          usedDefault: !!res.data?.used_default,
          mockupIndex,
        },
      }));
      toast.success(
        res.data?.used_default
          ? "Test ran with built-in default prompt"
          : "Test ran with your draft prompt",
      );
    } catch (e) {
      setPromptTests((p) => ({
        ...p,
        [sz.id]: { ...(p[sz.id] || {}), loading: false, url: null },
      }));
      toast.error(e.response?.data?.detail || "Prompt test failed");
    }
  };

  const uploadFrame = async (sz, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [sz.id]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${apiBase}/admin/sizes/${sz.id}/frame-upload`, fd, {
        headers: {
          "X-Admin-Password": adminPassword,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`${sz.name} frame uploaded — inner rect auto-detected`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [sz.id]: false }));
    }
  };

  const uploadGalleryImage = async (sz, collection, style, file) => {
    if (!file) return;
    const key = `${sz.id}_gallery_${collection}_${style}`;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("collection", collection);
      fd.append("style", style);
      await axios.post(
        `${apiBase}/admin/sizes/${sz.id}/gallery-upload`,
        fd,
        {
          headers: {
            "X-Admin-Password": adminPassword,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success(`Image added → ${sz.name} / ${collection} / ${style}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gallery upload failed");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const deleteGalleryImage = async (sz, collection, style, idx) => {
    if (!window.confirm(`Remove this gallery image (${collection} / ${style})?`)) return;
    try {
      await axios.delete(
        `${apiBase}/admin/sizes/${sz.id}/gallery/${collection}/${style}/${idx}`,
        { headers: { "X-Admin-Password": adminPassword } },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const moveGalleryImage = async (sz, collection, style, fromIdx, toIdx) => {
    try {
      await axios.post(
        `${apiBase}/admin/sizes/${sz.id}/gallery/${collection}/${style}/reorder`,
        { from: fromIdx, to: toIdx },
        { headers: { "X-Admin-Password": adminPassword } },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border/60 p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="admin-sizes-panel">
      <h2 className="text-2xl font-bold mb-2">Print Products</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Configure each buyable product (frame size, canvas, acrylic stand,
        mug). The Qikink SKU drives fulfillment; the mockup image is used to
        render the customer preview. For mugs (or any product with a curved
        surface), use <span className="font-mono">nano-banana</span> mode so
        Gemini wraps the artwork onto a reference image instead of pasting it
        into a flat rect.
      </p>

      <div className="space-y-6">
        {items.map((sz) => {
          const fi = sz.frame_inner || null;
          const innerStr = fi ? `${fi.left},${fi.top},${fi.right},${fi.bottom}` : "";
          return (
            <div
              key={sz.id}
              className="border border-border/60 p-4 sm:p-5"
              data-testid={`admin-size-row-${sz.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
                {/* Mockup gallery — up to 6 per product. Each upload appends. */}
                <div>
                  {(() => {
                    const mockupList = (sz.mockups && sz.mockups.length > 0)
                      ? sz.mockups
                      : (sz.frame_image_url ? [{ url: sz.frame_image_url, inner: sz.frame_inner }] : []);
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          {mockupList.length === 0 && (
                            <div className="col-span-2 aspect-square bg-[#F3EEE6] border border-border/40 flex items-center justify-center">
                              <span className="text-xs text-muted-foreground px-3 text-center">
                                {sz.id === "a4" ? "Using bundled A4 mockup" : "No mockups yet"}
                              </span>
                            </div>
                          )}
                          {mockupList.map((m, idx) => (
                            <div
                              key={idx}
                              className="relative aspect-square bg-[#F3EEE6] border border-border/40 overflow-hidden group"
                              data-testid={`admin-size-mockup-${sz.id}-${idx}`}
                            >
                              <img
                                src={m.url}
                                alt={`${sz.name} mockup ${idx + 1}`}
                                className="w-full h-full object-contain"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (!window.confirm(`Remove mockup ${idx + 1}?`)) return;
                                  axios
                                    .delete(`${apiBase}/admin/sizes/${sz.id}/mockups/${idx}`, {
                                      headers: { "X-Admin-Password": adminPassword },
                                    })
                                    .then(() => { toast.success("Removed"); load(); })
                                    .catch((err) => toast.error(err.response?.data?.detail || "Delete failed"));
                                }}
                                className="absolute top-1 right-1 bg-white/90 text-[10px] px-1.5 py-0.5 border opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`admin-size-mockup-delete-${sz.id}-${idx}`}
                              >
                                ✕
                              </button>
                              <span className="absolute bottom-1 left-1 bg-white/90 text-[10px] px-1.5 py-0.5">
                                #{idx + 1}
                              </span>
                            </div>
                          ))}
                        </div>
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => uploadFrame(sz, e.target.files?.[0])}
                            data-testid={`admin-size-upload-${sz.id}`}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full text-xs"
                            asChild
                            disabled={uploading[sz.id] || mockupList.length >= 6}
                          >
                            <span className="cursor-pointer">
                              {uploading[sz.id] ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : null}
                              {mockupList.length === 0
                                ? "Upload mockup"
                                : mockupList.length >= 6
                                  ? "Max 6 reached"
                                  : `+ Add mockup (${mockupList.length}/6)`}
                            </span>
                          </Button>
                        </label>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Each variant generates one preview per mockup. Customers
                          swipe through them as a carousel.
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Right column: gallery + fields grouped in a single grid
                    child so the fields section keeps the full 1fr width
                    instead of wrapping onto the 180px column of the next
                    row (which was rendering labels as vertical stacks of
                    single letters). */}
                <div className="space-y-4 min-w-0">
                {/* Display gallery — separate from AI-composition mockups.
                    Nested 3-level hierarchy so admins can scope an image to:
                      - "Common" → every style page on the site
                      - "<collection> / Collection-wide" → every style in a
                        given collection
                      - "<collection> / <style>" → ONE specific style only
                    The customer-facing thumbnail strip on /style and /promo
                    pulls the union of common + collection-wide + style
                    bucket for the user's current selections. */}
                {(() => {
                  const galleryAll = sz.gallery_images || {};
                  const commonBucket = Array.isArray(galleryAll.common)
                    ? galleryAll.common
                    : (galleryAll.common || []);
                  return (
                    <div
                      className="border border-border/60 bg-muted/30 p-3 space-y-3"
                      data-testid={`admin-size-galleries-${sz.id}`}
                    >
                      <Label className="label-uppercase block">
                        Product Gallery Images
                      </Label>

                      {/* === Common (universal) === */}
                      <GalleryBucket
                        label="Common · shown on every style page"
                        urls={commonBucket}
                        size={sz}
                        collection="common"
                        style="common"
                        uploading={uploading}
                        onUpload={uploadGalleryImage}
                        onDelete={deleteGalleryImage}
                        onMove={moveGalleryImage}
                        testid={`admin-size-gallery-bucket-${sz.id}-common`}
                      />

                      {/* === Per-collection nested blocks === */}
                      {collections.map((c) => {
                        const cBucket = galleryAll[c.id];
                        const collectionCommon =
                          (cBucket && typeof cBucket === "object" && !Array.isArray(cBucket))
                            ? (cBucket.common || [])
                            : (Array.isArray(cBucket) ? cBucket : []);
                        const styleBuckets =
                          cBucket && typeof cBucket === "object" && !Array.isArray(cBucket)
                            ? cBucket
                            : {};
                        // Total count to show in the collapsible summary.
                        let totalInCollection = collectionCommon.length;
                        Object.entries(styleBuckets).forEach(([k, v]) => {
                          if (k !== "common" && Array.isArray(v)) totalInCollection += v.length;
                        });
                        return (
                          <details
                            key={c.id}
                            className="border border-border/40 bg-card"
                            data-testid={`admin-size-gallery-coll-${sz.id}-${c.id}`}
                          >
                            <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-foreground flex items-center justify-between">
                              <span>{c.name}</span>
                              <span className="text-[10px] text-muted-foreground font-normal">
                                {totalInCollection} image{totalInCollection === 1 ? "" : "s"}
                              </span>
                            </summary>
                            <div className="px-3 pb-3 space-y-2">
                              {/* Collection-wide */}
                              <GalleryBucket
                                label={`${c.name} · whole collection`}
                                urls={collectionCommon}
                                size={sz}
                                collection={c.id}
                                style="common"
                                uploading={uploading}
                                onUpload={uploadGalleryImage}
                                onDelete={deleteGalleryImage}
                                onMove={moveGalleryImage}
                                testid={`admin-size-gallery-bucket-${sz.id}-${c.id}-common`}
                              />
                              {/* Per-style sub-buckets */}
                              {c.styles.map((st) => {
                                const styleUrls = Array.isArray(styleBuckets[st.id])
                                  ? styleBuckets[st.id]
                                  : [];
                                return (
                                  <GalleryBucket
                                    key={st.id}
                                    label={`${st.name}`}
                                    urls={styleUrls}
                                    size={sz}
                                    collection={c.id}
                                    style={st.id}
                                    uploading={uploading}
                                    onUpload={uploadGalleryImage}
                                    onDelete={deleteGalleryImage}
                                    onMove={moveGalleryImage}
                                    testid={`admin-size-gallery-bucket-${sz.id}-${c.id}-${st.id}`}
                                  />
                                );
                              })}
                            </div>
                          </details>
                        );
                      })}

                      <p className="text-[10px] text-muted-foreground leading-tight">
                        Common images show on every style page. Collection-wide
                        images show on every style in that collection.
                        Style-specific images show only on that one style. The
                        customer&apos;s thumbnail strip is the union of all
                        three buckets that apply to their current selection.
                      </p>
                    </div>
                  );
                })()}

                {/* Fields */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1 block">Display Name</Label>
                      <Input
                        value={draftFor(sz.id, "name", sz.name)}
                        onChange={(e) => setDraft(sz.id, { name: e.target.value })}
                        data-testid={`admin-size-name-${sz.id}`}
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block">Qikink SKU</Label>
                      <Input
                        value={draftFor(sz.id, "qikink_sku", sz.qikink_sku)}
                        onChange={(e) => setDraft(sz.id, { qikink_sku: e.target.value })}
                        data-testid={`admin-size-sku-${sz.id}`}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1 block">Width (inches)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={draftFor(sz.id, "width_inches", sz.width_inches)}
                        onChange={(e) => setDraft(sz.id, { width_inches: parseFloat(e.target.value) })}
                        data-testid={`admin-size-w-${sz.id}`}
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block">Height (inches)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={draftFor(sz.id, "height_inches", sz.height_inches)}
                        onChange={(e) => setDraft(sz.id, { height_inches: parseFloat(e.target.value) })}
                        data-testid={`admin-size-h-${sz.id}`}
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block">Sort Order</Label>
                      <Input
                        type="number"
                        value={draftFor(sz.id, "sort_order", sz.sort_order)}
                        onChange={(e) => setDraft(sz.id, { sort_order: parseInt(e.target.value, 10) })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1 block">Type</Label>
                      <select
                        className="h-9 w-full border border-border/60 bg-background px-2 text-sm"
                        value={draftFor(sz.id, "product_type", sz.product_type || "frame")}
                        onChange={(e) => setDraft(sz.id, { product_type: e.target.value })}
                        data-testid={`admin-size-type-${sz.id}`}
                      >
                        <option value="frame">Frame</option>
                        <option value="canvas">Canvas</option>
                        <option value="acrylic">Acrylic Stand</option>
                        <option value="mug">Coffee Mug</option>
                      </select>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block">Mockup Mode</Label>
                      <select
                        className="h-9 w-full border border-border/60 bg-background px-2 text-sm"
                        value={draftFor(sz.id, "mockup_mode", sz.mockup_mode || "auto_detect")}
                        onChange={(e) => setDraft(sz.id, { mockup_mode: e.target.value })}
                        data-testid={`admin-size-mockup-mode-${sz.id}`}
                      >
                        <option value="auto_detect">Auto-detect (paste in rect)</option>
                        <option value="nano_banana">Nano Banana (wrap on ref)</option>
                      </select>
                      <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
                        Both modes use the mockup uploaded on the left.
                        Auto-detect pastes art into the detected white area;
                        Nano Banana sends the mockup + art to Gemini and asks
                        it to render the artwork onto the product realistically.
                      </p>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block">Default?</Label>
                      <label className="flex items-center gap-2 text-sm h-9">
                        <input
                          type="checkbox"
                          checked={!!draftFor(sz.id, "is_default", sz.is_default)}
                          onChange={(e) => setDraft(sz.id, { is_default: e.target.checked })}
                          data-testid={`admin-size-default-${sz.id}`}
                        />
                        Storefront default
                      </label>
                    </div>
                  </div>
                  {/* Per-product Nano Banana wrap prompt. Only shown when
                      mode = nano_banana. Empty = use built-in default. */}
                  {(draftFor(sz.id, "mockup_mode", sz.mockup_mode || "auto_detect") === "nano_banana") && (
                    <div className="border border-border/60 bg-muted/30 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <Label className="label-uppercase">
                          Nano Banana Wrap Prompt
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setDraft(sz.id, { nano_banana_prompt: defaultNbPrompt })
                            }
                            disabled={!defaultNbPrompt}
                            data-testid={`admin-size-nb-prompt-reset-${sz.id}`}
                          >
                            Load default
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => runPromptTest(sz)}
                            disabled={
                              !!promptTests[sz.id]?.loading ||
                              (sz.mockups || []).length === 0
                            }
                            data-testid={`admin-size-nb-prompt-test-${sz.id}`}
                          >
                            {promptTests[sz.id]?.loading ? (
                              <>
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                Testing…
                              </>
                            ) : (
                              "Test prompt"
                            )}
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        rows={10}
                        placeholder={defaultNbPrompt || "Loading default prompt…"}
                        value={draftFor(sz.id, "nano_banana_prompt", sz.nano_banana_prompt || "")}
                        onChange={(e) =>
                          setDraft(sz.id, { nano_banana_prompt: e.target.value })
                        }
                        className="font-mono text-xs leading-relaxed"
                        data-testid={`admin-size-nb-prompt-${sz.id}`}
                      />
                      <p className="text-[11px] text-muted-foreground leading-tight">
                        This prompt is sent to Gemini Nano Banana along with
                        the mockup image (first) and the generated artwork
                        (second) to compose each preview. Leave empty to use
                        the built-in default. Use &quot;Load default&quot; to
                        populate the editor with the current default, then
                        customize. &quot;Test prompt&quot; runs a single
                        composition against the first mockup using a sample
                        pet artwork — no customer job consumed.
                      </p>
                      {(sz.mockups || []).length > 1 && (
                        <div className="flex items-center gap-2 text-xs">
                          <Label className="label-uppercase">Test against</Label>
                          <select
                            className="h-8 border border-border/60 bg-background px-2 text-xs"
                            value={promptTests[sz.id]?.mockupIndex || 0}
                            onChange={(e) =>
                              setPromptTests((p) => ({
                                ...p,
                                [sz.id]: {
                                  ...(p[sz.id] || {}),
                                  mockupIndex: parseInt(e.target.value, 10),
                                },
                              }))
                            }
                            data-testid={`admin-size-nb-prompt-mockup-${sz.id}`}
                          >
                            {(sz.mockups || []).map((_, i) => (
                              <option key={i} value={i}>
                                Mockup #{i + 1}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {promptTests[sz.id]?.url && (
                        <div className="mt-2">
                          <Label className="label-uppercase mb-1 block">
                            Test result
                            {promptTests[sz.id]?.usedDefault && (
                              <span className="ml-2 font-normal text-[10px] text-muted-foreground">
                                (used built-in default — textarea was empty)
                              </span>
                            )}
                          </Label>
                          <a
                            href={promptTests[sz.id].url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-size-nb-prompt-result-${sz.id}`}
                          >
                            <img
                              src={promptTests[sz.id].url}
                              alt="Prompt test result"
                              className="max-h-80 border border-border/40 object-contain"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <Label className="label-uppercase mb-1 block">
                      Frame Inner Rect (left,top,right,bottom in px)
                    </Label>
                    <Input
                      placeholder="e.g. 167,102,470,536"
                      value={draftFor(sz.id, "frame_inner_str", innerStr)}
                      onChange={(e) => setDraft(sz.id, { frame_inner_str: e.target.value })}
                      data-testid={`admin-size-inner-${sz.id}`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-detected when you upload a mockup. Override here if
                      the detection misses. Ignored when Mockup Mode is
                      <span className="font-mono"> nano_banana</span>.
                    </p>
                  </div>
                  {/* Nano-banana reference image — only relevant for legacy
                      records that uploaded a separate mug reference before
                      the unified upload existed. The standard "Upload mockup"
                      button on the left now serves both modes; if it's set
                      we use it for nano-banana too. This block stays so
                      admins can clear / replace a legacy reference. */}
                  {(draftFor(sz.id, "mockup_mode", sz.mockup_mode || "auto_detect") === "nano_banana") &&
                   sz.mug_reference_image_url && (
                    <div className="border border-dashed border-border/60 p-3">
                      <Label className="label-uppercase mb-2 block">
                        Legacy reference image
                      </Label>
                      <div className="flex items-center gap-3">
                        <img
                          src={sz.mug_reference_image_url}
                          alt={`${sz.name} reference`}
                          className="h-20 w-20 object-cover border border-border/40"
                          data-testid={`admin-size-mug-ref-${sz.id}`}
                        />
                        <label>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              const fd = new FormData();
                              fd.append("file", f);
                              axios
                                .post(
                                  `${apiBase}/admin/sizes/${sz.id}/mug-reference-upload`,
                                  fd,
                                  {
                                    headers: {
                                      "X-Admin-Password": adminPassword,
                                      "Content-Type": "multipart/form-data",
                                    },
                                  }
                                )
                                .then(() => {
                                  toast.success("Reference replaced");
                                  load();
                                })
                                .catch((err) =>
                                  toast.error(err.response?.data?.detail || "Upload failed")
                                );
                            }}
                            data-testid={`admin-size-mug-ref-upload-${sz.id}`}
                          />
                          <Button variant="outline" size="sm" asChild>
                            <span className="cursor-pointer">Replace</span>
                          </Button>
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Legacy: the standard mockup upload on the left now
                        powers both auto-detect <em>and</em> nano-banana modes.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={draftFor(sz.id, "active", sz.active)}
                        onChange={(e) => setDraft(sz.id, { active: e.target.checked })}
                      />
                      Active
                    </label>
                    <Button
                      size="sm"
                      onClick={() => save(sz)}
                      disabled={saving[sz.id]}
                      data-testid={`admin-size-save-${sz.id}`}
                    >
                      {saving[sz.id] ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Save
                    </Button>
                  </div>
                </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ====================== Photo Books Admin Panel ====================== */
// Three responsibilities:
//   1. Settings: price + headline/subheadline shown on the public landing.
//   2. Examples: gallery of sample books (image_url + caption + sort).
//   3. Orders: customer requests; admin updates status as the offline
//      workflow progresses (pending → paid → in_design → shipped).
const HOMEPAGE_COPY_FIELDS = [
  { key: "eyebrow", label: "Hero eyebrow", placeholder: "Pet portraits & gifts", rows: 1 },
  { key: "headline", label: "Hero headline", placeholder: "Make something personal for your pet.", rows: 2 },
  { key: "subheadline", label: "Hero subheadline", placeholder: "One line under the headline", rows: 2 },
  { key: "primary_cta_label", label: "Header CTA button label", placeholder: "Get started", rows: 1 },
  { key: "mobile_cta_label", label: "Mobile sticky CTA label", placeholder: "Get started", rows: 1 },
  { key: "final_eyebrow", label: "Bottom section eyebrow", placeholder: "Ready to begin?", rows: 1 },
  { key: "final_headline", label: "Bottom section headline", placeholder: "Create something personal for your home.", rows: 2 },
  { key: "final_subheadline", label: "Bottom section subheadline", placeholder: "One-liner under the bottom headline", rows: 2 },
  { key: "final_cta_label", label: "Bottom CTA button label", placeholder: "Get started", rows: 1 },
];

// Slots shown for "Shop by Product" homepage thumbnails. Should mirror the
// product_type values used by PRODUCT_HINTS in HomePage.js.
const SHOP_PRODUCT_SLOTS = [
  { key: "frame",   label: "Frame" },
  { key: "canvas",  label: "Canvas" },
  { key: "acrylic", label: "Acrylic Stand" },
  { key: "mug",     label: "Coffee Mug" },
];

const HomepagePanel = ({ adminPassword, apiBase }) => {
  const headers = useMemo(() => ({ "X-Admin-Password": adminPassword }), [adminPassword]);
  const [copy, setCopy] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCopy, setSavingCopy] = useState(false);
  const [savingSections, setSavingSections] = useState(false);
  // Per-product-type homepage thumbnail overrides ({frame: url, ...}).
  const [shopThumbs, setShopThumbs] = useState({});
  const [thumbBusy, setThumbBusy] = useState({}); // {frame: true, ...}

  const load = async () => {
    setLoading(true);
    try {
      const [hp, ss] = await Promise.all([
        axios.get(`${apiBase}/homepage-settings`),
        axios.get(`${apiBase}/site-settings`),
      ]);
      setCopy(hp.data.copy || {});
      setSections(hp.data.sections || []);
      setShopThumbs(ss.data?.shop_product_thumbnails || {});
    } catch {
      toast.error("Failed to load homepage settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCopy = async () => {
    setSavingCopy(true);
    try {
      const r = await axios.patch(
        `${apiBase}/admin/homepage-settings`,
        { copy },
        { headers }
      );
      setCopy(r.data.copy || copy);
      toast.success("Homepage text saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCopy(false);
    }
  };

  const moveSection = (idx, dir) => {
    const next = [...sections];
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSections(next);
  };
  const toggleVisible = (idx) => {
    const next = [...sections];
    next[idx] = { ...next[idx], visible: !next[idx].visible };
    setSections(next);
  };
  const saveSections = async () => {
    setSavingSections(true);
    try {
      const r = await axios.patch(
        `${apiBase}/admin/homepage-settings`,
        { sections: sections.map((s) => ({ id: s.id, visible: !!s.visible })) },
        { headers }
      );
      setSections(r.data.sections || sections);
      toast.success("Section order saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingSections(false);
    }
  };

  // Shop by Product thumbnail upload — uses the dedicated endpoint that
  // uploads + persists in one shot, then refreshes our local state.
  const uploadShopThumbnail = async (productType, file) => {
    if (!file) return;
    setThumbBusy((prev) => ({ ...prev, [productType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("product_type", productType);
      const r = await axios.post(
        `${apiBase}/admin/shop-product-thumbnail/upload`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      setShopThumbs((prev) => ({ ...prev, [productType]: r.data.url }));
      toast.success(`${productType} thumbnail updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setThumbBusy((prev) => ({ ...prev, [productType]: false }));
    }
  };

  const clearShopThumbnail = async (productType) => {
    setThumbBusy((prev) => ({ ...prev, [productType]: true }));
    try {
      const r = await axios.put(
        `${apiBase}/admin/site-settings`,
        { shop_product_thumbnails: { [productType]: "" } },
        { headers }
      );
      setShopThumbs(r.data?.shop_product_thumbnails || {});
      toast.success(`${productType} thumbnail cleared`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setThumbBusy((prev) => ({ ...prev, [productType]: false }));
    }
  };

  if (loading || !copy) {
    return (
      <div className="bg-card border border-border/60 p-6 sm:p-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8 space-y-10" data-testid="homepage-panel">
      <div>
        <h2 className="text-2xl font-bold mb-2">Homepage editor</h2>
        <p className="text-sm text-muted-foreground">
          Edit the hero copy, the bottom CTA section, and the visibility +
          order of every section on the homepage.
        </p>
      </div>

      {/* ---- Copy editor ---- */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Text &amp; CTAs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {HOMEPAGE_COPY_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="label-uppercase mb-1 block">{f.label}</Label>
              {f.rows > 1 ? (
                <textarea
                  rows={f.rows}
                  value={copy[f.key] || ""}
                  onChange={(e) => setCopy({ ...copy, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full bg-background border border-border/60 p-2 text-sm focus:outline-none focus:border-primary resize-y"
                  data-testid={`homepage-copy-${f.key}`}
                />
              ) : (
                <Input
                  value={copy[f.key] || ""}
                  onChange={(e) => setCopy({ ...copy, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="h-9"
                  data-testid={`homepage-copy-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Button
            onClick={saveCopy}
            disabled={savingCopy}
            data-testid="homepage-copy-save"
          >
            {savingCopy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save text
          </Button>
        </div>
      </div>

      {/* ---- Section order + visibility ---- */}
      <div className="border-t border-border/40 pt-8">
        <h3 className="text-lg font-semibold mb-1">Sections — order &amp; visibility</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Drag with the ↑↓ buttons to reorder. Toggle visibility to hide a
          section completely from the homepage.
        </p>
        <div className="space-y-2" data-testid="homepage-sections">
          {sections.map((s, i) => (
            <div
              key={s.id}
              className="flex items-center gap-3 border border-border/60 p-3"
              data-testid={`homepage-section-${s.id}`}
            >
              <span className="font-mono text-xs text-muted-foreground w-6">
                {i + 1}.
              </span>
              <span className="flex-1 text-sm">{s.label || s.id}</span>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!s.visible}
                  onChange={() => toggleVisible(i)}
                  data-testid={`homepage-section-${s.id}-visible`}
                  className="h-4 w-4 accent-[#C8A27A]"
                />
                Visible
              </label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => moveSection(i, "up")}
                disabled={i === 0}
                data-testid={`homepage-section-${s.id}-up`}
                className="px-2"
              >
                ↑
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => moveSection(i, "down")}
                disabled={i === sections.length - 1}
                data-testid={`homepage-section-${s.id}-down`}
                className="px-2"
              >
                ↓
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Button
            onClick={saveSections}
            disabled={savingSections}
            data-testid="homepage-sections-save"
          >
            {savingSections ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save sections
          </Button>
        </div>
      </div>

      {/* ---- Shop by Product thumbnails ---- */}
      <div className="border-t border-border/40 pt-8">
        <h3 className="text-lg font-semibold mb-1">Shop by Product — homepage thumbnails</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Upload the image that appears on each &ldquo;Shop by Product&rdquo; card
          on the homepage. Leave a slot empty to fall back to the default
          variant&apos;s first mockup.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="shop-product-thumbnails">
          {SHOP_PRODUCT_SLOTS.map((slot) => {
            const url = shopThumbs[slot.key] || "";
            const busy = !!thumbBusy[slot.key];
            return (
              <div
                key={slot.key}
                className="border border-border/60 p-3 flex flex-col"
                data-testid={`shop-thumb-${slot.key}`}
              >
                <p className="font-medium text-sm mb-2">{slot.label}</p>
                <div className="aspect-square w-full bg-[#F3EEE6] mb-3 overflow-hidden flex items-center justify-center">
                  {url ? (
                    <img
                      src={url}
                      alt={slot.label}
                      className="w-full h-full object-cover"
                      data-testid={`shop-thumb-${slot.key}-preview`}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground px-3 text-center">
                      No override — using default mockup
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadShopThumbnail(slot.key, e.target.files?.[0])}
                      data-testid={`shop-thumb-${slot.key}-upload`}
                    />
                    <Button size="sm" variant="outline" asChild disabled={busy} className="w-full">
                      <span className="cursor-pointer">
                        {busy ? (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        ) : null}
                        {url ? "Replace" : "Upload"}
                      </span>
                    </Button>
                  </label>
                  {url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => clearShopThumbnail(slot.key)}
                      disabled={busy}
                      data-testid={`shop-thumb-${slot.key}-clear`}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


const PhotoBookMediaCard = ({ example, index, total, onMove, onToggle, onDelete, onSaveCaption }) => {
  const [caption, setCaption] = useState(example.caption || "");
  const [editing, setEditing] = useState(false);
  // Keep local caption synced when the parent reloads after a save.
  useEffect(() => {
    setCaption(example.caption || "");
  }, [example.caption]);
  const isVideo = example.media_type === "video";
  return (
    <div
      className={`border ${example.active ? "border-border/60" : "border-dashed border-border/40 opacity-50"}`}
      data-testid={`admin-photo-book-example-${example.id}`}
    >
      <div className="aspect-square overflow-hidden bg-[#F3EEE6] relative">
        {isVideo ? (
          <video
            src={example.image_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            src={example.image_url}
            alt={example.caption || "example"}
            className="w-full h-full object-cover"
          />
        )}
        {isVideo && (
          <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 uppercase tracking-wide">
            Video
          </span>
        )}
      </div>
      <div className="p-2 text-xs space-y-2">
        {editing ? (
          <div className="flex gap-1">
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="flex-1 min-w-0 border border-border/60 px-2 py-1 text-xs"
              placeholder="Caption"
              data-testid={`admin-photo-book-caption-input-${example.id}`}
            />
            <Button
              size="sm"
              variant="default"
              onClick={() => {
                onSaveCaption(example, caption);
                setEditing(false);
              }}
              data-testid={`admin-photo-book-caption-save-${example.id}`}
            >
              Save
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="block w-full text-left text-muted-foreground hover:text-foreground truncate"
            data-testid={`admin-photo-book-caption-${example.id}`}
            title="Click to edit caption"
          >
            {example.caption || "Add caption…"}
          </button>
        )}
        <div className="flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onMove(example, "up")}
            disabled={index === 0}
            data-testid={`admin-photo-book-up-${example.id}`}
            title="Move up"
            className="px-2"
          >
            ↑
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onMove(example, "down")}
            disabled={index === total - 1}
            data-testid={`admin-photo-book-down-${example.id}`}
            title="Move down"
            className="px-2"
          >
            ↓
          </Button>
          <Button size="sm" variant="outline" onClick={() => onToggle(example)}>
            {example.active ? "Hide" : "Show"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(example)}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};


const PhotoBooksAdminPanel = ({ adminPassword, apiBase }) => {
  const [tab, setTab] = useState("examples");
  const headers = { "X-Admin-Password": adminPassword };

  // --- Settings state ---
  const [settings, setSettings] = useState({ price: 1999, headline: "", subheadline: "", hero_image_url: "" });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const r = await axios.get(`${apiBase}/admin/photo-book-settings`, { headers });
      setSettings({
        price: r.data?.price || 1999,
        headline: r.data?.headline || "",
        subheadline: r.data?.subheadline || "",
        hero_image_url: r.data?.hero_image_url || "",
      });
    } catch {
      /* ignore */
    } finally {
      setSettingsLoading(false);
    }
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.patch(
        `${apiBase}/admin/photo-book-settings`,
        {
          price: Number(settings.price) || 1999,
          headline: settings.headline,
          subheadline: settings.subheadline,
        },
        { headers }
      );
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };
  const onHeroUpload = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${apiBase}/admin/photo-book-hero/upload`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      setSettings((prev) => ({ ...prev, hero_image_url: r.data.url || "" }));
      toast.success("Hero image updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hero upload failed");
    } finally {
      setUploadingHero(false);
    }
  };

  // --- Examples state ---
  const [examples, setExamples] = useState([]);
  const [examplesLoading, setExamplesLoading] = useState(true);
  const [newCaption, setNewCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const loadExamples = async () => {
    setExamplesLoading(true);
    try {
      const r = await axios.get(`${apiBase}/admin/photo-book-examples`, { headers });
      setExamples(r.data.items || []);
    } catch {
      /* ignore */
    } finally {
      setExamplesLoading(false);
    }
  };
  const onExampleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upload = await axios.post(
        `${apiBase}/admin/photo-book-examples/upload`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      await axios.post(
        `${apiBase}/admin/photo-book-examples`,
        {
          image_url: upload.data.url,
          caption: newCaption,
          sort_order: examples.length,
          media_type: upload.data.media_type || "image",
        },
        { headers }
      );
      toast.success(
        (upload.data.media_type === "video" ? "Video" : "Image") + " added"
      );
      setNewCaption("");
      loadExamples();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const moveExample = async (ex, direction) => {
    try {
      await axios.post(
        `${apiBase}/admin/photo-book-examples/${ex.id}/move`,
        { direction },
        { headers }
      );
      loadExamples();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };
  const saveExampleCaption = async (ex, caption) => {
    try {
      await axios.patch(
        `${apiBase}/admin/photo-book-examples/${ex.id}`,
        { caption },
        { headers }
      );
      toast.success("Caption updated");
      loadExamples();
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const toggleExample = async (ex) => {
    try {
      await axios.patch(
        `${apiBase}/admin/photo-book-examples/${ex.id}`,
        { active: !ex.active },
        { headers }
      );
      loadExamples();
    } catch (e) {
      toast.error("Update failed");
    }
  };
  const deleteExample = async (ex) => {
    if (!window.confirm("Delete this example?")) return;
    try {
      await axios.delete(`${apiBase}/admin/photo-book-examples/${ex.id}`, { headers });
      loadExamples();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // --- Orders state ---
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const r = await axios.get(`${apiBase}/admin/photo-book-orders`, { headers });
      setOrders(r.data.items || []);
    } catch {
      /* ignore */
    } finally {
      setOrdersLoading(false);
    }
  };
  const updateOrderStatus = async (id, status) => {
    try {
      await axios.patch(
        `${apiBase}/admin/photo-book-orders/${id}`,
        { status },
        { headers }
      );
      toast.success(`Status → ${status}`);
      loadOrders();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const [sR, eR, oR] = await Promise.all([
          axios.get(`${apiBase}/admin/photo-book-settings`, { headers, signal: ctrl.signal }),
          axios.get(`${apiBase}/admin/photo-book-examples`, { headers, signal: ctrl.signal }),
          axios.get(`${apiBase}/admin/photo-book-orders`, { headers, signal: ctrl.signal }),
        ]);
        setSettings({
          price: sR.data?.price || 1999,
          headline: sR.data?.headline || "",
          subheadline: sR.data?.subheadline || "",
          hero_image_url: sR.data?.hero_image_url || "",
        });
        setExamples(eR.data?.items || []);
        setOrders(oR.data?.items || []);
      } catch {
        /* ignore */
      } finally {
        setSettingsLoading(false);
        setExamplesLoading(false);
        setOrdersLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, adminPassword]);

  return (
    <div className="bg-card border border-border/60 p-6 sm:p-8" data-testid="admin-photo-books-panel">
      <div className="flex border-b border-border/60 mb-6 gap-1">
        {[
          ["examples", "Examples"],
          ["orders", `Orders (${orders.length})`],
          ["settings", "Settings"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`admin-photo-books-subtab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "examples" && (
        <div>
          <div className="border border-dashed border-border/60 p-4 mb-5">
            <Label className="label-uppercase mb-2 block">Add a sample book image or video</Label>
            <Input
              placeholder="Optional caption"
              value={newCaption}
              onChange={(e) => setNewCaption(e.target.value)}
              className="h-9 mb-3 max-w-md"
              data-testid="admin-photo-book-new-caption"
            />
            <label>
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => onExampleUpload(e.target.files?.[0])}
                data-testid="admin-photo-book-upload"
              />
              <Button variant="outline" size="sm" asChild disabled={uploading}>
                <span className="cursor-pointer">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Upload image or video
                </span>
              </Button>
            </label>
            <p className="text-xs text-muted-foreground mt-2">
              Drop in any image (JPG/PNG/WEBP) or video (MP4/MOV/WEBM). The order
              you set here is the order customers see on the product page.
            </p>
          </div>
          {examplesLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {examples.map((ex, idx) => (
                <PhotoBookMediaCard
                  key={ex.id}
                  example={ex}
                  index={idx}
                  total={examples.length}
                  onMove={moveExample}
                  onToggle={toggleExample}
                  onDelete={deleteExample}
                  onSaveCaption={saveExampleCaption}
                />
              ))}
              {examples.length === 0 && (
                <p className="col-span-full text-sm text-muted-foreground">No examples yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div>
          {ordersLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : orders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No photo book orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 px-3">Ref</th>
                    <th className="py-2 px-3">Customer</th>
                    <th className="py-2 px-3">Contact</th>
                    <th className="py-2 px-3">Variant</th>
                    <th className="py-2 px-3">Coupon</th>
                    <th className="py-2 px-3">Amount</th>
                    <th className="py-2 px-3">Notes</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3">Update</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={o.id}
                      className="border-b border-border/60 align-top"
                      data-testid={`admin-photo-book-order-${o.id}`}
                    >
                      <td className="py-2 px-3 font-mono text-xs">{o.reference_number}</td>
                      <td className="py-2 px-3">{o.customer_name}</td>
                      <td className="py-2 px-3 text-xs">
                        <div className="break-all" data-testid={`admin-photo-book-email-${o.id}`}>
                          {o.email || <span className="text-muted-foreground">—</span>}
                        </div>
                        <div className="text-muted-foreground" data-testid={`admin-photo-book-phone-${o.id}`}>
                          {o.phone || "—"}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {o.variant_name || <span className="text-muted-foreground">—</span>}
                        {o.variant_mrp && o.variant_mrp !== o.amount && (
                          <div className="text-muted-foreground">
                            MRP ₹{o.variant_mrp} − ₹{o.variant_discount || 0}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {o.coupon_code ? (
                          <>
                            <div className="font-mono">{o.coupon_code}</div>
                            {o.coupon_label && (
                              <div className="text-muted-foreground">{o.coupon_label}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3">₹{(o.amount || 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground max-w-xs whitespace-pre-wrap">
                        {o.notes || "—"}
                      </td>
                      <td className="py-2 px-3 text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          o.status === "shipped" ? "bg-green-100 text-green-800" :
                          o.status === "paid" ? "bg-blue-100 text-blue-800" :
                          o.status === "in_design" ? "bg-amber-100 text-amber-800" :
                          "bg-gray-100 text-gray-700"
                        }`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs whitespace-nowrap">{fmtIST(o.created_at)}</td>
                      <td className="py-2 px-3">
                        <select
                          value={o.status}
                          onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                          className="h-8 border border-border/60 bg-background px-2 text-xs"
                          data-testid={`admin-photo-book-order-status-${o.id}`}
                        >
                          <option value="pending">pending</option>
                          <option value="paid">paid</option>
                          <option value="in_design">in_design</option>
                          <option value="shipped">shipped</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-4 max-w-2xl">
          {settingsLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <div>
                <Label className="label-uppercase mb-1 block">Price (₹)</Label>
                <Input
                  type="number"
                  value={settings.price}
                  onChange={(e) => setSettings({ ...settings, price: e.target.value })}
                  data-testid="admin-photo-book-settings-price"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1 block">Headline</Label>
                <Input
                  value={settings.headline}
                  onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
                  data-testid="admin-photo-book-settings-headline"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1 block">Subheadline</Label>
                <textarea
                  rows={3}
                  value={settings.subheadline}
                  onChange={(e) => setSettings({ ...settings, subheadline: e.target.value })}
                  className="w-full bg-background border border-border/60 p-2 text-sm focus:outline-none focus:border-primary resize-y"
                  data-testid="admin-photo-book-settings-subheadline"
                />
              </div>

              {/* Hero image — shown on the homepage's Photo Books promo strip
                  AND at the top of the product page. */}
              <div className="border border-dashed border-border/60 p-4">
                <Label className="label-uppercase mb-2 block">
                  Homepage hero image
                </Label>
                {settings.hero_image_url ? (
                  <img
                    src={settings.hero_image_url}
                    alt="Photo book hero"
                    className="max-w-xs w-full aspect-video object-cover mb-3 border border-border/40"
                    data-testid="admin-photo-book-hero-preview"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground mb-3">
                    No hero image set — the homepage will fall back to the
                    default design.
                  </p>
                )}
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onHeroUpload(e.target.files?.[0])}
                    data-testid="admin-photo-book-hero-upload"
                  />
                  <Button variant="outline" size="sm" asChild disabled={uploadingHero}>
                    <span className="cursor-pointer">
                      {uploadingHero ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {settings.hero_image_url ? "Replace hero image" : "Upload hero image"}
                    </span>
                  </Button>
                </label>
              </div>

              {/* Variant pricing — read-only info card, since the three
                  Father's Day variants are baked into the backend. */}
              <div className="border border-border/60 p-4 bg-[#FAF6EF]">
                <Label className="label-uppercase mb-2 block">
                  Product variants (with Inaugural Offer auto-apply)
                </Label>
                <ul className="text-xs text-[#1A1A1A] space-y-1">
                  <li>• Storybook Video — MRP ₹1499 → <strong>₹1199</strong> (Save ₹300 default · ₹400 with BHAIRUSFRIEND)</li>
                  <li>• A5 Hardcover Storybook — MRP ₹2299 → <strong>₹1799</strong> (Save ₹500 default · ₹600 with BHAIRUSFRIEND)</li>
                  <li>• Storybook + Video Bundle — MRP ₹2599 → <strong>₹1999</strong> (Save ₹600 default · ₹700 with BHAIRUSFRIEND) · Most Loved</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  The Inaugural Offer applies automatically. Customers can type
                  <strong> BHAIRUSFRIEND</strong> on the product page for a bigger discount.
                </p>
              </div>

              <Button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="admin-photo-book-settings-save"
              >
                {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
